import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

let cityAmbientEcoBombs = [], cityAmmo = 0, cityCooldown = 0, cityCraterRings = [], cityEffectBursts = [], cityExplosions = [], cityFireHeld = false, cityFlyThrustCooldownFrames = 0, cityGameLevel = 1, cityHeroKey = null, cityLastCannonMs = 0, cityLastSpaceDownMs = 0, cityLifeGroup = new THREE.Group(), cityMonsterGroup = new THREE.Group(), cityMonsterWavePendingRespawn = false, cityMonsters = [], cityNuclearAfterglow = 0, cityNuclearWaves = [], cityPitch = 0, cityPlayerHp = 0, cityPlayerPos = null, cityRockets = [], cityRoot = new THREE.Group(), citySyncParcelAccum = 0, cityTvBurnCd = 0, cityTvParalyzeCd = 0, cityVertVel = 0, cityWeapon = null, cityYaw = 0;

const CONFIG = {
  court: { halfX: 3.8, halfY: 2.2 },
  paddle: { halfY: 0.55, halfZ: 0.12, halfX: 0.14 },
  ballRadius: 0.065,
  baseSpeed: 5.1,
  maxSpeed: 12,
  hitSpeedMul: 1.08,
  aiSpeed: 3.15,
  aiDeadzone: 0.12,
  winScore: 11,
  playerMove: 9.5,
  ballSpeedStep: 0.09,
  ballSpeedMinMul: 0.38,
  ballSpeedMaxMul: 1.35,
  pongCameraPos: new THREE.Vector3(0, 7.75, 8.85),
  /** Cada N goles (suma de ambos jugadores) la pelota crece en modo sandbox. */
  pongSandboxGrowEvery: 10,
  pongSandboxGrowFactor: 1.16,
  pongSandboxMaxRadiusMul: 2.45,
};

/** Radio de la pelota en Pong = `CONFIG.ballRadius * pongBallRadiusMul`. */
let pongBallRadiusMul = 1;
let pongSandbox = false;

function getPongBallRadius() {
  return CONFIG.ballRadius * pongBallRadiusMul;
}

const keyCodes = ['KeyW', 'KeyS', 'KeyA', 'KeyD', 'ArrowUp', 'ArrowDown', 'Space'];
const keys = Object.fromEntries(keyCodes.map((c) => [c, false]));

/** @type {AudioContext | null} */
let audioCtx = null;

function getAudioContext() {
  if (!audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    audioCtx = new AC();
  }
  return audioCtx;
}

function resumeAudio() {
  const ctx = getAudioContext();
  if (ctx?.state === 'suspended') ctx.resume().catch(() => {});
}

function playBallWallBounce() {
  const ctx = getAudioContext();
  if (!ctx) return;
  const t = ctx.currentTime;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(380, t);
  osc.frequency.exponentialRampToValueAtTime(165, t + 0.055);
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.11, t + 0.004);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.075);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(t);
  osc.stop(t + 0.085);
}

function playBallPaddleHit(speed) {
  const ctx = getAudioContext();
  if (!ctx) return;
  const t = ctx.currentTime;
  const mul = THREE.MathUtils.clamp(speed / CONFIG.maxSpeed, 0.2, 1);
  const f0 = 280 + mul * 340;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'triangle';
  osc.frequency.setValueAtTime(f0, t);
  osc.frequency.exponentialRampToValueAtTime(f0 * 0.55, t + 0.045);
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.15, t + 0.003);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.095);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(t);
  osc.stop(t + 0.11);
}

function playCannonFire() {
  const ctx = getAudioContext();
  if (!ctx) return;
  const t = ctx.currentTime;
  const dur = 0.2;
  const n = Math.floor(ctx.sampleRate * dur);
  const buffer = ctx.createBuffer(1, n, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < n; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / n) ** 1.2;
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const bp = ctx.createBiquadFilter();
  bp.type = 'lowpass';
  bp.frequency.setValueAtTime(2200, t);
  bp.frequency.exponentialRampToValueAtTime(380, t + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.22, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  src.connect(bp);
  bp.connect(g);
  g.connect(ctx.destination);
  src.start(t);

  const boom = ctx.createOscillator();
  boom.type = 'sine';
  boom.frequency.setValueAtTime(88, t);
  boom.frequency.exponentialRampToValueAtTime(42, t + 0.16);
  const g2 = ctx.createGain();
  g2.gain.setValueAtTime(0.32, t);
  g2.gain.exponentialRampToValueAtTime(0.001, t + 0.19);
  boom.connect(g2);
  g2.connect(ctx.destination);
  boom.start(t);
  boom.stop(t + 0.21);
}

function playBombExplosion() {
  const ctx = getAudioContext();
  if (!ctx) return;
  const t = ctx.currentTime;
  const dur = 0.38;
  const n = Math.floor(ctx.sampleRate * dur);
  const buffer = ctx.createBuffer(1, n, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < n; i++) {
    const e = 1 - i / n;
    data[i] = (Math.random() * 2 - 1) * e * e;
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(650, t);
  lp.frequency.exponentialRampToValueAtTime(90, t + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.38, t + 0.015);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  src.connect(lp);
  lp.connect(g);
  g.connect(ctx.destination);
  src.start(t);
}

function playNuclearExplosion() {
  const ctx = getAudioContext();
  if (!ctx) return;
  const t = ctx.currentTime;
  const dur = 2.4;
  const n = Math.floor(ctx.sampleRate * dur);
  const buffer = ctx.createBuffer(1, n, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < n; i++) {
    const e = 1 - i / n;
    const rumble = (Math.random() * 2 - 1) * (0.55 + 0.45 * e);
    data[i] = rumble * e * e;
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(420, t);
  lp.frequency.exponentialRampToValueAtTime(35, t + dur * 0.92);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(0.52, t + 0.04);
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  src.connect(lp);
  lp.connect(g);
  g.connect(ctx.destination);
  src.start(t);

  const crackDur = 0.55;
  const n2 = Math.floor(ctx.sampleRate * crackDur);
  const buf2 = ctx.createBuffer(1, n2, ctx.sampleRate);
  const d2 = buf2.getChannelData(0);
  for (let i = 0; i < n2; i++) {
    const e = 1 - i / n2;
    d2[i] = (Math.random() * 2 - 1) * e;
  }
  const src2 = ctx.createBufferSource();
  src2.buffer = buf2;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.setValueAtTime(1800, t);
  bp.frequency.exponentialRampToValueAtTime(200, t + crackDur);
  bp.Q.value = 0.7;
  const g2 = ctx.createGain();
  g2.gain.setValueAtTime(0.22, t);
  g2.gain.exponentialRampToValueAtTime(0.001, t + crackDur);
  src2.connect(bp);
  bp.connect(g2);
  g2.connect(ctx.destination);
  src2.start(t);

  const sub = ctx.createOscillator();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(48, t);
  sub.frequency.exponentialRampToValueAtTime(18, t + 1.1);
  const g3 = ctx.createGain();
  g3.gain.setValueAtTime(0.38, t);
  g3.gain.exponentialRampToValueAtTime(0.001, t + 1.35);
  sub.connect(g3);
  g3.connect(ctx.destination);
  sub.start(t);
  sub.stop(t + 1.4);
}

window.addEventListener('keydown', (e) => {
  if (e.code in keys) keys[e.code] = true;
});
window.addEventListener('keyup', (e) => {
  if (e.code in keys) keys[e.code] = false;
});

window.addEventListener(
  'keydown',
  (e) => {
    if (e.code !== 'Space' || gameMode !== 'city' || cityFrozen) return;
    e.preventDefault();
    if (e.repeat) return;
    const now = performance.now();
    const dtMax = CONFIG.city.flyDoubleTapMs;
    if (cityLastSpaceDownMs > 0 && now - cityLastSpaceDownMs < dtMax) {
      cityPlayerPos.y = CITY_GROUND_Y;
      cityVertVel = 0;
      cityLastSpaceDownMs = 0;
      cityFlyThrustCooldownFrames = 5;
    } else {
      cityLastSpaceDownMs = now;
      cityVertVel = Math.max(cityVertVel, CONFIG.city.flyTapImpulse);
    }
  },
  true
);

const menuOverlay = document.getElementById('menu-overlay');
const btnPongAi = document.getElementById('btn-pong-ai');
const btnPong2p = document.getElementById('btn-pong-2p');
const btnCity = document.getElementById('btn-city');
const heroOverlay = document.getElementById('hero-overlay');
const btnHeroStart = document.getElementById('btn-hero-start');
const btnHeroCam = document.getElementById('btn-hero-cam');
const btnHeroSpike = document.getElementById('btn-hero-spike');
const btnHeroTitan = document.getElementById('btn-hero-titan');
const heroNameInput = document.getElementById('hero-name');
const elCrosshair = document.getElementById('crosshair');
const elCityBanner = document.getElementById('city-banner');
const toMenu = document.getElementById('to-menu');
const elScore = document.getElementById('score');
const elBallSpeedWrap = document.getElementById('ball-speed-wrap');
const elBallSpeed = document.getElementById('ball-speed');
const elHelpInner = document.getElementById('help-inner');

let elP1 = document.getElementById('p1');
let elP2 = document.getElementById('p2');
let elCityHp = null;
let elCityMon = null;
let elCityLevel = null;
let elCityHero = null;

/** @type {'menu' | 'pong'} */
let gameMode = 'menu';
let vsRobot = true;

let ballSpeedMul = 0.55;
let ballVel = new THREE.Vector2(0, 0);
let ballPos = new THREE.Vector2(0, 0);

/* Drag-to-move táctil: una Z mundial objetivo por paleta (null = teclado). */
let touchPlayerTargetZ = null;
let touchAiTargetZ = null;
let touchPlayerPointerId = null;
let touchAiPointerId = null;

const isTouchDevice = (() => {
  if ('ontouchstart' in window) return true;
  if ((navigator.maxTouchPoints || 0) > 0) return true;
  if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return true;
  if (typeof location !== 'undefined' && /[?&]touch=1/.test(location.search)) return true;
  return false;
})();

function getEffectiveBaseSpeed() {
  return CONFIG.baseSpeed * ballSpeedMul;
}

function getEffectiveMaxSpeed() {
  return CONFIG.maxSpeed * ballSpeedMul;
}

function setBallSpeedMul(next) {
  const clamped = THREE.MathUtils.clamp(
    next,
    CONFIG.ballSpeedMinMul,
    CONFIG.ballSpeedMaxMul
  );
  const prev = ballSpeedMul;
  if (prev <= 0) return;
  ballSpeedMul = clamped;
  const ratio = clamped / prev;
  if (gameMode === 'city') {
    const cap = 85 * ballSpeedMul;
    for (const r of cityRockets) {
      r.vel.multiplyScalar(ratio);
      if (r.vel.length() > cap) r.vel.normalize().multiplyScalar(cap);
    }
  } else if (ballVel.lengthSq() > 1e-8) {
    ballVel.multiplyScalar(ratio);
    const cap = getEffectiveMaxSpeed();
    if (ballVel.length() > cap) ballVel.normalize().multiplyScalar(cap);
  }
  if (elBallSpeed) elBallSpeed.textContent = `${Math.round(ballSpeedMul * 100)}%`;
}

window.addEventListener('keydown', (e) => {
  if (gameMode === 'menu') return;
  if (e.repeat) return;
  if (e.code === 'KeyP' || e.code === 'NumpadSubtract') {
    setBallSpeedMul(ballSpeedMul - CONFIG.ballSpeedStep);
    e.preventDefault();
  } else if (e.code === 'KeyR' || e.code === 'NumpadAdd') {
    setBallSpeedMul(ballSpeedMul + CONFIG.ballSpeedStep);
    e.preventDefault();
  } else if (e.code === 'KeyQ' || e.code === 'KeyE') {
    // Reserved for potential future use (was city hero abilities)
  }
});

const scene = new THREE.Scene();
scene.background = null;
scene.fog = new THREE.Fog(0xf5c4b8, 28, 92);

const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.08, 220);
camera.position.copy(CONFIG.pongCameraPos);
camera.lookAt(0, 0, 0);

const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.08;
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.setClearColor(0x5a78c8, 1);
document.body.appendChild(renderer.domElement);

const pmrem = new THREE.PMREMGenerator(renderer);
scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.09).texture;

const SKY_SUN_DIR = new THREE.Vector3(0.38, 0.14, 0.82).normalize();

function createRobloxDawnSky() {
  const skyGeo = new THREE.SphereGeometry(120, 64, 48);
  const sunDir = SKY_SUN_DIR.clone();
  const skyMat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      uZenith: { value: new THREE.Color(0x5a78c8) },
      uHigh: { value: new THREE.Color(0x8b9fd9) },
      uBelt: { value: new THREE.Color(0xe8a8c8) },
      uHorizon: { value: new THREE.Color(0xff8f62) },
      uHaze: { value: new THREE.Color(0xffdcc8) },
      uGlow: { value: new THREE.Color(0xffccaa) },
      uSunDir: { value: sunDir.clone() },
    },
    vertexShader: `
      varying vec3 vDir;
      void main() {
        vDir = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 uZenith, uHigh, uBelt, uHorizon, uHaze, uGlow;
      uniform vec3 uSunDir;
      varying vec3 vDir;

      void main() {
        vec3 dir = normalize(vDir);
        float y = dir.y;

        float tLow = smoothstep(-0.2, 0.08, y);
        float tBelt = smoothstep(0.02, 0.28, y);
        float tHigh = smoothstep(0.18, 0.55, y);
        float tZen = smoothstep(0.45, 0.92, y);

        vec3 c = mix(uHorizon, uBelt, tLow);
        c = mix(c, uHigh, tBelt);
        c = mix(c, uZenith, tHigh);
        c = mix(c, uZenith, tZen * 0.85);

        float haze = exp(-max(y + 0.02, 0.0) * 2.8) * 0.22;
        c += uHaze * haze;

        float w = sin(dir.x * 6.2 + dir.z * 4.1) * 0.5 + 0.5;
        float w2 = sin(dir.x * 2.7 - dir.y * 9.0 + dir.z * 3.4) * 0.5 + 0.5;
        float clouds = smoothstep(0.42, 0.78, w * 0.55 + w2 * 0.45);
        clouds *= smoothstep(-0.05, 0.35, y) * smoothstep(0.88, 0.28, y);
        c = mix(c, c + vec3(0.045, 0.05, 0.06), clouds * 0.28);

        float sunAlign = max(dot(dir, normalize(uSunDir)), 0.0);
        float sunHalo = pow(sunAlign, 4.0);
        c += uGlow * sunHalo * 0.22;
        float sunBloom = pow(sunAlign, 18.0) * 0.38;
        c += uGlow * sunBloom;
        float sunCore = pow(sunAlign, 256.0) * 0.82;
        c += vec3(1.0, 0.98, 0.92) * sunCore;

        gl_FragColor = vec4(c, 1.0);
      }
    `,
  });
  const sky = new THREE.Mesh(skyGeo, skyMat);
  sky.frustumCulled = false;
  sky.renderOrder = -1000;
  return sky;
}
scene.add(createRobloxDawnSky());

const hemi = new THREE.HemisphereLight(0xa8c4f0, 0xf0a888, 0.48);
scene.add(hemi);
const ambient = new THREE.AmbientLight(0xf0e8f2, 0.32);
scene.add(ambient);
const sun = new THREE.DirectionalLight(0xfff0e0, 1.05);
sun.position.copy(SKY_SUN_DIR).multiplyScalar(56);
sun.castShadow = true;
sun.shadow.mapSize.set(2048, 2048);
sun.shadow.camera.near = 0.5;
sun.shadow.camera.far = 95;
sun.shadow.camera.left = -55;
sun.shadow.camera.right = 55;
sun.shadow.camera.top = 55;
sun.shadow.camera.bottom = -55;
sun.shadow.bias = -0.00028;
scene.add(sun);
sun.target.position.set(35, 0, 0);
scene.add(sun.target);
const rim = new THREE.DirectionalLight(0xaab8ff, 0.38);
rim.position.set(8, 6, -10);
scene.add(rim);

const { halfX, halfY } = CONFIG.court;
const tableMat = new THREE.MeshStandardMaterial({
  color: 0x4a3428,
  roughness: 0.68,
  metalness: 0.02,
  envMapIntensity: 0.34,
});
const table = new THREE.Mesh(
  new THREE.BoxGeometry(halfX * 2 + 0.6, 0.14, halfY * 2 + 0.45),
  tableMat
);
table.position.y = -0.07;
table.receiveShadow = true;
scene.add(table);

const lineMat = new THREE.MeshStandardMaterial({
  color: 0xfff5ee,
  roughness: 0.78,
  metalness: 0,
  emissive: 0xffc4b0,
  emissiveIntensity: 0.22,
});
const centerLine = new THREE.Mesh(new THREE.BoxGeometry(0.03, 0.001, halfY * 2), lineMat);
centerLine.position.set(0, 0.071, 0);
centerLine.receiveShadow = true;
scene.add(centerLine);

const edgeMat = new THREE.MeshStandardMaterial({ color: 0x2a1e28, roughness: 0.88, metalness: 0 });
const edgeT = new THREE.Mesh(new THREE.BoxGeometry(halfX * 2 + 0.08, 0.06, 0.12), edgeMat);
edgeT.position.set(0, 0.05, -halfY - 0.02);
edgeT.castShadow = true;
edgeT.receiveShadow = true;
const edgeB = edgeT.clone();
edgeB.position.z = halfY + 0.02;
scene.add(edgeT, edgeB);

/** Anillo / rectángulo de juego sobre la mesa (área de rebote). */
const pongRingY = 0.079;
const pongRingPts = [
  new THREE.Vector3(-halfX, pongRingY, -halfY),
  new THREE.Vector3(halfX, pongRingY, -halfY),
  new THREE.Vector3(halfX, pongRingY, halfY),
  new THREE.Vector3(-halfX, pongRingY, halfY),
];
const pongRingGeo = new THREE.BufferGeometry().setFromPoints(pongRingPts);
const pongCourtRing = new THREE.LineLoop(
  pongRingGeo,
  new THREE.LineBasicMaterial({
    color: 0xfff4dc,
    transparent: true,
    opacity: 0.92,
    depthTest: true,
  })
);
pongCourtRing.renderOrder = 1;
scene.add(pongCourtRing);

const pongRingOuterPts = [
  new THREE.Vector3(-halfX - 0.06, pongRingY + 0.001, -halfY - 0.06),
  new THREE.Vector3(halfX + 0.06, pongRingY + 0.001, -halfY - 0.06),
  new THREE.Vector3(halfX + 0.06, pongRingY + 0.001, halfY + 0.06),
  new THREE.Vector3(-halfX - 0.06, pongRingY + 0.001, halfY + 0.06),
];
const pongCourtRingOuter = new THREE.LineLoop(
  new THREE.BufferGeometry().setFromPoints(pongRingOuterPts),
  new THREE.LineBasicMaterial({
    color: 0xffc8a8,
    transparent: true,
    opacity: 0.45,
    depthTest: true,
  })
);
pongCourtRingOuter.renderOrder = 0;
scene.add(pongCourtRingOuter);

const cityEnvironmentGroup = new THREE.Group();
cityEnvironmentGroup.visible = false;

const grassMat = new THREE.MeshStandardMaterial({
  color: 0x3a7a42,
  roughness: 0.92,
  metalness: 0,
  envMapIntensity: 0.12,
});
const grassMat2 = new THREE.MeshStandardMaterial({
  color: 0x2f6a38,
  roughness: 0.9,
  metalness: 0,
  envMapIntensity: 0.1,
});
const cityGrass = new THREE.Mesh(new THREE.PlaneGeometry(5200, 5200), grassMat);
cityGrass.rotation.x = -Math.PI / 2;
cityGrass.position.set(0, 0.002, 0);
cityGrass.receiveShadow = true;
cityEnvironmentGroup.add(cityGrass);

const grassPatch = new THREE.Mesh(new THREE.PlaneGeometry(900, 900), grassMat2);
grassPatch.rotation.x = -Math.PI / 2;
grassPatch.position.set(48, 0.003, 8);
grassPatch.receiveShadow = true;
cityEnvironmentGroup.add(grassPatch);

const asphaltMat = new THREE.MeshStandardMaterial({
  color: 0x2a2c30,
  roughness: 0.78,
  metalness: 0.15,
  envMapIntensity: 0.2,
});
function addRoadPlane(w, d, x, z, rotY = 0) {
  const r = new THREE.Mesh(new THREE.PlaneGeometry(w, d), asphaltMat);
  r.rotation.x = -Math.PI / 2;
  r.rotation.y = rotY;
  r.position.set(x, 0.008, z);
  r.receiveShadow = true;
  cityEnvironmentGroup.add(r);
  return r;
}

addRoadPlane(260, 5.4, 54, 1, 0);
addRoadPlane(160, 5.4, 22, 2, Math.PI / 2);
addRoadPlane(135, 5.4, 58, -10, Math.PI / 2);
addRoadPlane(115, 5.4, 80, 14, Math.PI / 2);
addRoadPlane(95, 4.2, 102, -6, 0);

/** Center + half-extents in xz (must stay in sync with addRoadPlane above). */
const CITY_ROAD_BLOCKS = [
  { cx: 54, cz: 1, hx: 130, hz: 2.7 },
  { cx: 22, cz: 2, hx: 2.7, hz: 80 },
  { cx: 58, cz: -10, hx: 2.7, hz: 67.5 },
  { cx: 80, cz: 14, hx: 2.7, hz: 57.5 },
  { cx: 102, cz: -6, hx: 47.5, hz: 2.1 },
];
/** Extra margin so huellas no rocen el asfalto. */
const CITY_ROAD_CLEARANCE = 1.2;

function xzAabbIntersectsAnyRoad(minX, maxX, minZ, maxZ) {
  const p = CITY_ROAD_CLEARANCE;
  for (let i = 0; i < CITY_ROAD_BLOCKS.length; i++) {
    const b = CITY_ROAD_BLOCKS[i];
    const rMinX = b.cx - b.hx - p;
    const rMaxX = b.cx + b.hx + p;
    const rMinZ = b.cz - b.hz - p;
    const rMaxZ = b.cz + b.hz + p;
    if (maxX >= rMinX && minX <= rMaxX && maxZ >= rMinZ && minZ <= rMaxZ) return true;
  }
  return false;
}

/** Peatones: cuerpo ~radio en planta. */
function isRoughlyOnRoad(px, pz) {
  const pe = 0.88;
  return xzAabbIntersectsAnyRoad(px - pe, px + pe, pz - pe, pz + pe);
}

/** Torre/casa: toda la base + separación no puede cruzar carretera. */
function buildingFootprintOnRoad(px, pz, w, d) {
  const pad = CONFIG.city.buildingFootprintPad + 0.42;
  return xzAabbIntersectsAnyRoad(
    px - w * 0.5 - pad,
    px + w * 0.5 + pad,
    pz - d * 0.5 - pad,
    pz + d * 0.5 + pad
  );
}

const paddleGeo = new THREE.BoxGeometry(
  CONFIG.paddle.halfX * 2,
  CONFIG.paddle.halfZ * 2,
  CONFIG.paddle.halfY * 2
);
const paddlePlayerMat = new THREE.MeshPhysicalMaterial({
  color: 0x3b7dff,
  roughness: 0.38,
  metalness: 0.06,
  clearcoat: 0.45,
  clearcoatRoughness: 0.35,
  envMapIntensity: 0.65,
});
const paddleAiMat = new THREE.MeshPhysicalMaterial({
  color: 0xff4d5c,
  roughness: 0.38,
  metalness: 0.06,
  clearcoat: 0.45,
  clearcoatRoughness: 0.35,
  envMapIntensity: 0.65,
});

const playerPaddle = new THREE.Mesh(paddleGeo, paddlePlayerMat);
const aiPaddle = new THREE.Mesh(paddleGeo, paddleAiMat);
const paddleX = halfX - CONFIG.paddle.halfX - 0.12;
const paddleY = CONFIG.ballRadius + CONFIG.paddle.halfZ + 0.02;
playerPaddle.position.set(-paddleX, paddleY, 0);
aiPaddle.position.set(paddleX, paddleY, 0);
playerPaddle.castShadow = true;
aiPaddle.castShadow = true;
playerPaddle.receiveShadow = true;
aiPaddle.receiveShadow = true;
scene.add(playerPaddle, aiPaddle);

const ballMat = new THREE.MeshPhysicalMaterial({
  color: 0xfff8f2,
  roughness: 0.18,
  metalness: 0,
  clearcoat: 0.65,
  clearcoatRoughness: 0.2,
  envMapIntensity: 0.95,
  emissive: 0xffaa77,
  emissiveIntensity: 0.22,
});
const ball = new THREE.Mesh(new THREE.SphereGeometry(CONFIG.ballRadius, 56, 36), ballMat);
ball.castShadow = true;
ball.receiveShadow = true;
scene.add(ball);

function syncPongBallVisual() {
  ball.scale.setScalar(pongBallRadiusMul);
  ball.position.y = getPongBallRadius() + 0.001;
}

syncPongBallVisual();

/** @type {THREE.InstancedMesh | null} */
let cityBuildingsInstanced = null;
let cityBuildingCount = 0;
/** @type {Float32Array | null} */
let buildingBoxMin = null;
/** @type {Float32Array | null} */
let buildingBoxMax = null;
/** @type {Map<string, number[]>} */
let buildingSpatialGrid = new Map();
let buildingSpatialDirty = true;
/** @type {Uint32Array | null} */
let buildingSegVisitStamp = null;
let buildingSegVisitGen = 1;

/** Parcel index per building (streaming). */
/** @type {Int16Array | null} */
let buildingParcelIx = null;
/** @type {Int16Array | null} */
let buildingParcelIz = null;
const cityLoadedParcels = new Set();
/** Parcelas donde no cabía ni una torre (carretera, etc.): no reintentar en bucle. */
const cityParcelSkipped = new Set();
const cityParcelTowerCounts = new Map();

const _ibuildPos = new THREE.Vector3();
const _ibuildScale = new THREE.Vector3();
const _ibuildMat = new THREE.Matrix4();
const _ibuildQuat = new THREE.Quaternion();
const _tmpSegSample = new THREE.Vector3();
/** Scratch indices for batch-removing buildings hit by nuclear waves (avoid GC). */
const _waveBuildingHitScratch = [];

/** @type {{ group: THREE.Group; age: number; maxAge: number }[]} */
let cityDebrisChunks = [];
let _rubbleSharedMat = null;

function getRubbleSharedMaterial() {
  if (!_rubbleSharedMat) {
    _rubbleSharedMat = new THREE.MeshStandardMaterial({
      color: 0x7c8898,
      roughness: 0.52,
      metalness: 0.38,
      envMapIntensity: 0.55,
    });
  }
  return _rubbleSharedMat;
}

function disposeRubbleGroup(gr) {
  const shared = _rubbleSharedMat;
  gr.traverse((o) => {
    if (!o.isMesh) return;
    o.geometry?.dispose();
    const m = o.material;
    if (m && m !== shared) m.dispose();
  });
}

function clearCityDebris() {
  for (const c of cityDebrisChunks) {
    scene.remove(c.group);
    disposeRubbleGroup(c.group);
  }
  cityDebrisChunks.length = 0;
}

/**
 * @param {number} bi
 * @param {'full' | 'light'} mode
 */
function spawnBuildingDebrisForIndex(bi, mode) {
  if (bi < 0 || !buildingBoxMin || bi >= cityBuildingCount) return;
  const maxG = CONFIG.city.debrisMaxGroups;
  while (cityDebrisChunks.length >= maxG) {
    const old = cityDebrisChunks.shift();
    scene.remove(old.group);
    disposeRubbleGroup(old.group);
  }

  const o = bi * 3;
  const minX = buildingBoxMin[o];
  const minY = buildingBoxMin[o + 1];
  const minZ = buildingBoxMin[o + 2];
  const maxX = buildingBoxMax[o];
  const maxY = buildingBoxMax[o + 1];
  const maxZ = buildingBoxMax[o + 2];
  const cx = (minX + maxX) * 0.5;
  const cy = (minY + maxY) * 0.5;
  const cz = (minZ + maxZ) * 0.5;
  const sx = maxX - minX;
  const sy = maxY - minY;
  const sz = maxZ - minZ;
  const sm = Math.max(0.12, Math.min(sx, sy, sz) * 0.22);

  const mesh = cityBuildingsInstanced;
  const cr = mesh?.instanceColor?.array;
  const rr = cr ? cr[o] : 0.55;
  const gg = cr ? cr[o + 1] : 0.58;
  const bb = cr ? cr[o + 2] : 0.62;

  const full = mode === 'full';
  const nMin = full ? CONFIG.city.debrisPiecesFullMin : CONFIG.city.debrisPiecesLightMin;
  const nMax = full ? CONFIG.city.debrisPiecesFullMax : CONFIG.city.debrisPiecesLightMax;
  const n = THREE.MathUtils.clamp(
    THREE.MathUtils.randInt(nMin, nMax),
    1,
    full ? 28 : 12
  );

  const baseMat = getRubbleSharedMaterial();
  const g = new THREE.Group();
  const imp = full ? 1 : 0.55;

  for (let i = 0; i < n; i++) {
    const us = sm * (0.35 + Math.random() * 0.95) * imp;
    const geo = new THREE.BoxGeometry(us, us * (0.6 + Math.random() * 1.1), us * (0.5 + Math.random() * 0.9));
    const pieceMat = baseMat.clone();
    pieceMat.color.setRGB(
      rr * (0.82 + Math.random() * 0.22),
      gg * (0.82 + Math.random() * 0.22),
      bb * (0.82 + Math.random() * 0.22)
    );
    const m = new THREE.Mesh(geo, pieceMat);
    m.castShadow = true;
    m.receiveShadow = true;
    m.position.set(
      cx + (Math.random() - 0.5) * sx * 0.42,
      cy + (Math.random() - 0.5) * sy * 0.42,
      cz + (Math.random() - 0.5) * sz * 0.42
    );
    m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
    m.userData.v = new THREE.Vector3(
      (Math.random() - 0.5) * 9 * imp,
      2.5 + Math.random() * 11 * imp,
      (Math.random() - 0.5) * 9 * imp
    );
    m.userData.w = new THREE.Vector3(
      (Math.random() - 0.5) * 14,
      (Math.random() - 0.5) * 14,
      (Math.random() - 0.5) * 14
    );
    g.add(m);
  }

  scene.add(g);
  cityDebrisChunks.push({ group: g, age: 0, maxAge: CONFIG.city.debrisLifetime + Math.random() * 0.45 });
}

function updateCityDebris(dt) {
  const gFall = 16;
  for (let i = cityDebrisChunks.length - 1; i >= 0; i--) {
    const ch = cityDebrisChunks[i];
    ch.age += dt;
    ch.group.children.forEach((m) => {
      if (!m.userData.v) return;
      m.position.addScaledVector(m.userData.v, dt);
      m.userData.v.y -= gFall * dt;
      m.rotation.x += m.userData.w.x * dt;
      m.rotation.y += m.userData.w.y * dt;
      m.rotation.z += m.userData.w.z * dt;
    });
    if (ch.age >= ch.maxAge) {
      scene.remove(ch.group);
      disposeRubbleGroup(ch.group);
      cityDebrisChunks.splice(i, 1);
    }
  }
}

let cityFrozen = false;
let cityBannerTimer = 0;
let cityLose = false;

function ensureBuildingCollisionBuffers() {
  const cap = CONFIG.city.buildingMaxCount;
  const n = cap * 3;
  if (!buildingBoxMin || buildingBoxMin.length < n) {
    buildingBoxMin = new Float32Array(n);
    buildingBoxMax = new Float32Array(n);
  }
  if (!buildingSegVisitStamp || buildingSegVisitStamp.length < cap) {
    buildingSegVisitStamp = new Uint32Array(cap);
  }
  if (!buildingParcelIx || buildingParcelIx.length < cap) {
    buildingParcelIx = new Int16Array(cap);
    buildingParcelIz = new Int16Array(cap);
    buildingParcelIx.fill(-512);
    buildingParcelIz.fill(-512);
  }
}

function disposeCityInstancedBuildings() {
  if (!cityBuildingsInstanced) return;
  cityRoot.remove(cityBuildingsInstanced);
  cityBuildingsInstanced.geometry?.dispose();
  const mat = cityBuildingsInstanced.material;
  if (mat && !Array.isArray(mat)) {
    mat.map = null;
    mat.emissiveMap = null;
    mat.dispose();
  }
  cityBuildingsInstanced = null;
  cityBuildingCount = 0;
}

function clearCityBuildings() {
  cityLoadedParcels.clear();
  cityParcelSkipped.clear();
  cityParcelTowerCounts.clear();
  disposeCityInstancedBuildings();
  buildingSpatialGrid.clear();
  buildingSpatialDirty = true;
}

function fillSegHitBoxFromBuildingIndex(bi) {
  const o = bi * 3;
  _segHitBox.min.set(buildingBoxMin[o], buildingBoxMin[o + 1], buildingBoxMin[o + 2]);
  _segHitBox.max.set(buildingBoxMax[o], buildingBoxMax[o + 1], buildingBoxMax[o + 2]);
}

function rebuildBuildingSpatialGrid() {
  buildingSpatialGrid.clear();
  const cell = CONFIG.city.buildingSpatialCell;
  const inv = 1 / cell;
  for (let i = 0; i < cityBuildingCount; i++) {
    const o = i * 3;
    const mnX = buildingBoxMin[o];
    const mnY = buildingBoxMin[o + 1];
    const mnZ = buildingBoxMin[o + 2];
    const mxX = buildingBoxMax[o];
    const mxY = buildingBoxMax[o + 1];
    const mxZ = buildingBoxMax[o + 2];
    const cx0 = Math.floor(mnX * inv);
    const cx1 = Math.floor(mxX * inv);
    const cz0 = Math.floor(mnZ * inv);
    const cz1 = Math.floor(mxZ * inv);
    for (let cx = cx0; cx <= cx1; cx++) {
      for (let cz = cz0; cz <= cz1; cz++) {
        const key = `${cx},${cz}`;
        let arr = buildingSpatialGrid.get(key);
        if (!arr) {
          arr = [];
          buildingSpatialGrid.set(key, arr);
        }
        arr.push(i);
      }
    }
  }
  buildingSpatialDirty = false;
}

/**
 * @param {number} bi
 * @param {'none' | 'light' | 'full'} [debrisMode]
 */
function removeCityBuildingAtIndex(bi, debrisMode = 'none') {
  if (bi < 0 || bi >= cityBuildingCount || !cityBuildingsInstanced) return;
  if (debrisMode === 'full' || debrisMode === 'light') {
    spawnBuildingDebrisForIndex(bi, debrisMode);
  }
  const vx = buildingParcelIx[bi];
  const vz = buildingParcelIz[bi];
  const vk = `${vx},${vz}`;
  const last = cityBuildingCount - 1;
  const mesh = cityBuildingsInstanced;
  if (bi !== last) {
    const oB = bi * 3;
    const oL = last * 3;
    for (let k = 0; k < 3; k++) {
      buildingBoxMin[oB + k] = buildingBoxMin[oL + k];
      buildingBoxMax[oB + k] = buildingBoxMax[oL + k];
    }
    mesh.getMatrixAt(last, _ibuildMat);
    mesh.setMatrixAt(bi, _ibuildMat);
    const ca = mesh.instanceColor.array;
    ca[oB] = ca[oL];
    ca[oB + 1] = ca[oL + 1];
    ca[oB + 2] = ca[oL + 2];
    buildingParcelIx[bi] = buildingParcelIx[last];
    buildingParcelIz[bi] = buildingParcelIz[last];
  }
  cityBuildingCount--;
  mesh.count = cityBuildingCount;
  mesh.instanceMatrix.needsUpdate = true;
  mesh.instanceColor.needsUpdate = true;
  buildingSpatialDirty = true;

  const n = cityParcelTowerCounts.get(vk);
  if (n !== undefined) {
    const u = n - 1;
    if (u <= 0) {
      cityParcelTowerCounts.delete(vk);
      cityLoadedParcels.delete(vk);
    } else {
      cityParcelTowerCounts.set(vk, u);
    }
  }
}

function clearCityRockets() {
  for (const r of cityRockets) {
    scene.remove(r.group);
    r.group.traverse((ch) => {
      if (ch.geometry) ch.geometry.dispose();
      if (ch.material && !Array.isArray(ch.material)) ch.material.dispose();
    });
  }
  cityRockets = [];
}

function clearCityExplosions() {
  for (const ex of cityExplosions) {
    scene.remove(ex.light);
    ex.light.dispose();
  }
  cityExplosions = [];
  for (const b of cityEffectBursts) {
    scene.remove(b.group);
    deepDisposeCityProp(b.group);
  }
  cityEffectBursts = [];
  for (const ab of cityAmbientEcoBombs) {
    scene.remove(ab.group);
    deepDisposeCityProp(ab.group);
  }
  cityAmbientEcoBombs = [];
}

function clearNuclearTerrainMarks() {
  cityNuclearWaves.length = 0;
  for (const ring of cityCraterRings) {
    cityEnvironmentGroup.remove(ring);
    ring.geometry?.dispose();
    const m = ring.material;
    if (m && !Array.isArray(m)) m.dispose();
  }
  cityCraterRings.length = 0;
}

/** @type {{ group: THREE.Group; rotors: THREE.Object3D[]; phase: number; baseX: number; baseY: number; baseZ: number }[]} */
let cityDrones = [];
/** @type {{ group: THREE.Group; axis: 'x' | 'z'; pos: number; speed: number; min: number; max: number }[]} */
let cityCars = [];
/** @type {{ group: THREE.Group; vel: THREE.Vector3; xMin: number; xMax: number }[]} */
let cityPlanes = [];

function deepDisposeCityProp(root) {
  const geoms = new Set();
  const mats = new Set();
  root.traverse((o) => {
    if (o.geometry && !geoms.has(o.geometry)) {
      geoms.add(o.geometry);
      o.geometry.dispose();
    }
    const ms = o.material;
    if (!ms) return;
    for (const m of Array.isArray(ms) ? ms : [ms]) {
      if (!m || mats.has(m)) continue;
      mats.add(m);
      if (m.map) m.map.dispose();
      if (m.emissiveMap) m.emissiveMap.dispose();
      m.dispose();
    }
  });
}

function clearCityLife() {
  cityDrones.length = 0;
  cityCars.length = 0;
  cityPlanes.length = 0;
  if (!cityLifeGroup) return;
  while (cityLifeGroup.children.length > 0) {
    const ch = cityLifeGroup.children[0];
    cityLifeGroup.remove(ch);
    deepDisposeCityProp(ch);
  }
}

function clearCityMonsters() {
  if (!cityMonsterGroup) { cityMonsters.length = 0; return; }
  cityMonsters.length = 0;
  while (cityMonsterGroup.children.length > 0) {
    const ch = cityMonsterGroup.children[0];
    cityMonsterGroup.remove(ch);
    deepDisposeCityProp(ch);
  }
}

function createMonsterGroup() {
  const g = new THREE.Group();
  const skin = new THREE.MeshStandardMaterial({
    color: new THREE.Color().setHSL(0.28 + Math.random() * 0.06, 0.72, 0.22),
    roughness: 0.88,
    metalness: 0.06,
  });
  const belly = new THREE.MeshStandardMaterial({
    color: new THREE.Color().setHSL(0.83 + Math.random() * 0.08, 0.55, 0.32),
    roughness: 0.62,
    metalness: 0.18,
  });
  const eye = new THREE.MeshStandardMaterial({
    color: 0xffee44,
    emissive: 0xaa6600,
    emissiveIntensity: 0.55,
    roughness: 0.35,
  });
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.52, 0.62, 6, 10), skin);
  body.position.y = 0.85;
  const gut = new THREE.Mesh(new THREE.SphereGeometry(0.38, 10, 8), belly);
  gut.position.set(0.06, 0.72, 0.22);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.36, 10, 8), skin);
  head.position.set(0, 1.52, 0.1);
  const hornL = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.38, 6), skin);
  hornL.position.set(0.22, 1.78, 0);
  hornL.rotation.z = -0.55;
  const hornR = hornL.clone();
  hornR.position.x = -0.22;
  hornR.rotation.z = 0.55;
  const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.09, 6, 6), eye);
  eyeL.position.set(0.14, 1.56, 0.32);
  const eyeR = eyeL.clone();
  eyeR.position.x = -0.14;
  g.add(body, gut, head, hornL, hornR, eyeL, eyeR);
  g.traverse((ch) => {
    if (ch.isMesh) ch.castShadow = true;
  });
  return g;
}

function spawnCityMonsters() {
  clearCityMonsters();
  const lv = THREE.MathUtils.clamp(cityGameLevel, 1, CITY_MAX_LEVEL);
  const n = Math.max(
    0,
    Math.floor(CONFIG.city.monsterCount + (lv - 1) * 4 + (lv >= CITY_MAX_LEVEL ? 6 : 0))
  );
  if (n === 0) return;
  const hpEach =
    CONFIG.city.monsterHp + (lv - 1) * 22 + (lv >= CITY_MAX_LEVEL ? 80 : 0);
  const spread = 58 + lv * 10;
  const cx = cityPlayerPos.x;
  const cz = cityPlayerPos.z;
  for (let i = 0; i < n; i++) {
    let x = 0;
    let z = 0;
    let placed = false;
    for (let t = 0; t < 140; t++) {
      x = cx + (Math.random() - 0.5) * spread * 2;
      z = cz + (Math.random() - 0.5) * spread * 2;
      if (!isRoughlyOnRoad(x, z)) {
        placed = true;
        break;
      }
    }
    if (!placed) continue;
    const group = createMonsterGroup();
    group.position.set(x, 0, z);
    group.rotation.y = Math.random() * Math.PI * 2;
    cityMonsterGroup.add(group);
    cityMonsters.push({ group, hp: hpEach, frozenUntil: 0 });
  }
}

function createPersonGroup() {
  const g = new THREE.Group();
  const shirtHue = Math.random();
  const shirt = new THREE.MeshStandardMaterial({
    color: new THREE.Color().setHSL(shirtHue, 0.62 + Math.random() * 0.32, 0.38 + Math.random() * 0.22),
    roughness: 0.72,
  });
  const pants = new THREE.MeshStandardMaterial({
    color: new THREE.Color().setHSL(0.58 + Math.random() * 0.08, 0.28, 0.22 + Math.random() * 0.12),
    roughness: 0.86,
  });
  const skin = new THREE.MeshStandardMaterial({ color: 0xd8a898, roughness: 0.78 });
  const hr = Math.random();
  const hairHSL =
    hr < 0.38
      ? { h: 0.07, s: 0.28 + Math.random() * 0.2, l: 0.06 + Math.random() * 0.14 }
      : hr < 0.78
        ? { h: 0.08, s: 0.42 + Math.random() * 0.2, l: 0.16 + Math.random() * 0.18 }
        : { h: 0.11, s: 0.5 + Math.random() * 0.25, l: 0.42 + Math.random() * 0.2 };
  const hairMat = new THREE.MeshStandardMaterial({
    color: new THREE.Color().setHSL(hairHSL.h, hairHSL.s, hairHSL.l),
    roughness: 0.88,
  });
  const eyeMat = new THREE.MeshStandardMaterial({ color: 0x1a1a22, roughness: 0.35 });

  const legs = new THREE.Mesh(new THREE.CapsuleGeometry(0.12, 0.4, 4, 8), pants);
  legs.position.y = 0.22;
  const torso = new THREE.Mesh(new THREE.BoxGeometry(0.46, 0.34, 0.26), shirt);
  torso.position.y = 0.58;
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.072, 0.08, 0.12, 8), skin);
  neck.position.y = 0.86;
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.15, 12, 10), skin);
  head.position.y = 1.02;
  const hair = new THREE.Mesh(new THREE.SphereGeometry(0.152, 10, 8), hairMat);
  hair.position.set(0, 1.08, -0.02);
  hair.scale.set(1.05, 0.62, 1.05);

  const eyeGeo = new THREE.SphereGeometry(0.028, 6, 6);
  const eyeL = new THREE.Mesh(eyeGeo, eyeMat);
  eyeL.position.set(-0.045, 1.03, 0.12);
  const eyeR = new THREE.Mesh(eyeGeo, eyeMat);
  eyeR.position.set(0.045, 1.03, 0.12);

  const shL = new THREE.Vector3(-0.24, 0.64, 0.04);
  const wrL = new THREE.Vector3(-0.48, 0.5, 0.07);
  const lenL = shL.distanceTo(wrL);
  const dirL = new THREE.Vector3().subVectors(wrL, shL).normalize();
  const midL = new THREE.Vector3().addVectors(shL, wrL).multiplyScalar(0.5);
  const armL = new THREE.Mesh(new THREE.BoxGeometry(0.086, 0.086, lenL), shirt);
  armL.position.copy(midL);
  armL.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), dirL);

  const shR = new THREE.Vector3(0.24, 0.64, 0.04);
  const wrR = new THREE.Vector3(0.48, 0.5, 0.07);
  const lenR = shR.distanceTo(wrR);
  const dirR = new THREE.Vector3().subVectors(wrR, shR).normalize();
  const midR = new THREE.Vector3().addVectors(shR, wrR).multiplyScalar(0.5);
  const armR = new THREE.Mesh(new THREE.BoxGeometry(0.086, 0.086, lenR), shirt);
  armR.position.copy(midR);
  armR.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), dirR);

  const handGeo = new THREE.SphereGeometry(0.056, 8, 6);
  const handL = new THREE.Mesh(handGeo, skin);
  handL.position.copy(wrL);
  const handR = new THREE.Mesh(handGeo.clone(), skin);
  handR.position.copy(wrR);

  g.add(legs, torso, neck, head, hair, eyeL, eyeR, armL, armR, handL, handR);
  g.traverse((ch) => {
    if (ch.isMesh) ch.castShadow = true;
  });
  return g;
}

function createCarGroup() {
  const g = new THREE.Group();
  const paint = new THREE.MeshStandardMaterial({
    color: new THREE.Color().setHSL(Math.random(), 0.52, 0.36 + Math.random() * 0.18),
    roughness: 0.28,
    metalness: 0.42,
    envMapIntensity: 0.58,
  });
  const glass = new THREE.MeshStandardMaterial({
    color: 0x223044,
    roughness: 0.12,
    metalness: 0.65,
    envMapIntensity: 0.85,
  });
  const rubber = new THREE.MeshStandardMaterial({ color: 0x121214, roughness: 0.94 });
  const chrome = new THREE.MeshStandardMaterial({
    color: 0xd8dde8,
    roughness: 0.22,
    metalness: 0.85,
    envMapIntensity: 0.9,
  });
  const lower = new THREE.Mesh(new THREE.BoxGeometry(0.66, 0.2, 1.18), paint);
  lower.position.y = 0.26;
  const cabin = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.2, 0.62), paint);
  cabin.position.set(0, 0.48, -0.04);
  const wind = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.14, 0.52), glass);
  wind.position.set(0, 0.52, 0.12);
  const grill = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.08, 0.06), chrome);
  grill.position.set(0, 0.28, 0.58);
  const bumperF = new THREE.Mesh(new THREE.BoxGeometry(0.64, 0.06, 0.08), chrome);
  bumperF.position.set(0, 0.14, 0.62);
  const bumperR = new THREE.Mesh(new THREE.BoxGeometry(0.64, 0.06, 0.08), chrome);
  bumperR.position.set(0, 0.14, -0.62);
  const lightL = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.05, 0.04), chrome);
  lightL.position.set(0.22, 0.26, 0.58);
  const lightR = lightL.clone();
  lightR.position.x = -0.22;

  const wGeo = new THREE.CylinderGeometry(0.12, 0.12, 0.08, 12);
  const wp = [
    [0.36, 0.12, 0.44],
    [-0.36, 0.12, 0.44],
    [0.36, 0.12, -0.44],
    [-0.36, 0.12, -0.44],
  ];
  for (const [x, y, z] of wp) {
    const w = new THREE.Mesh(wGeo, rubber);
    w.rotation.z = Math.PI / 2;
    w.position.set(x, y, z);
    g.add(w);
  }
  g.add(lower, cabin, wind, grill, bumperF, bumperR, lightL, lightR);
  g.traverse((ch) => {
    if (ch.isMesh) ch.castShadow = true;
  });
  return g;
}

function createDroneGroup() {
  const g = new THREE.Group();
  const bodyM = new THREE.MeshStandardMaterial({ color: 0x2c323c, roughness: 0.42, metalness: 0.55 });
  const core = new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.065, 0.15), bodyM);
  const armA = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.022, 0.045), bodyM);
  const armB = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.022, 0.55), bodyM);
  const rotors = [];
  const offsets = [
    [0.28, 0.04, 0],
    [-0.28, 0.04, 0],
    [0, 0.04, 0.28],
    [0, 0.04, -0.28],
  ];
  for (const [x, y, z] of offsets) {
    const blade = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.012, 6), bodyM);
    blade.position.set(x, y + 0.02, z);
    g.add(blade);
    rotors.push(blade);
  }
  const led = new THREE.MeshStandardMaterial({
    color: 0xff2222,
    emissive: 0xaa0000,
    emissiveIntensity: 0.45,
  });
  const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.028, 6, 6), led);
  bulb.position.set(0, -0.04, 0.07);
  g.add(core, armA, armB, bulb);
  g.traverse((ch) => {
    if (ch.isMesh) ch.castShadow = true;
  });
  return { group: g, rotors };
}

function createAirplaneGroup() {
  const g = new THREE.Group();
  const fuse = new THREE.MeshStandardMaterial({
    color: 0xf2f4fa,
    roughness: 0.4,
    metalness: 0.28,
    envMapIntensity: 0.5,
  });
  const fuselage = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.09, 2.4, 12), fuse);
  fuselage.rotation.z = Math.PI / 2;
  const wing = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.045, 2.65), fuse);
  wing.position.set(0.15, 0, 0);
  const tail = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.045, 0.14), fuse);
  tail.position.set(-0.95, 0.06, 0);
  g.add(fuselage, wing, tail);
  g.traverse((ch) => {
    if (ch.isMesh) {
      ch.castShadow = true;
      ch.receiveShadow = true;
    }
  });
  return { group: g };
}

function spawnCityLife() {
  clearCityLife();
  for (let i = 0; i < 36; i++) {
    let x = 0;
    let z = 0;
    let placed = false;
    for (let t = 0; t < 80; t++) {
      x = -88 + Math.random() * 268;
      z = -66 + Math.random() * 142;
      if (!isRoughlyOnRoad(x, z)) {
        placed = true;
        break;
      }
    }
    if (!placed) continue;
    const person = createPersonGroup();
    person.position.set(x, 0, z);
    person.rotation.y = Math.random() * Math.PI * 2;
    cityLifeGroup.add(person);
  }

  const carSpecs = [
    { axis: 'x', fixedZ: 0.45, fixedX: 0, min: 5, max: 122, speed: 12 },
    { axis: 'x', fixedZ: -0.95, fixedX: 0, min: 8, max: 116, speed: -14 },
    { axis: 'z', fixedX: 17.9, fixedZ: 0, min: -30, max: 40, speed: 10 },
    { axis: 'z', fixedX: 57.1, fixedZ: 0, min: -32, max: 38, speed: -11 },
    { axis: 'x', fixedZ: 0.25, fixedX: 0, min: 22, max: 105, speed: 16 },
    { axis: 'z', fixedX: 79.8, fixedZ: 0, min: -18, max: 48, speed: 9 },
  ];
  for (let i = 0; i < 14; i++) {
    const spec = carSpecs[i % carSpecs.length];
    const car = createCarGroup();
    const p0 = spec.min + Math.random() * (spec.max - spec.min);
    const jitter = (Math.random() - 0.5) * 0.9;
    if (spec.axis === 'x') {
      car.position.set(p0, 0.12, spec.fixedZ + jitter);
      car.rotation.y = spec.speed >= 0 ? -Math.PI / 2 : Math.PI / 2;
      cityCars.push({
        group: car,
        axis: 'x',
        pos: car.position.x,
        speed: spec.speed * (0.82 + Math.random() * 0.35),
        min: spec.min,
        max: spec.max,
      });
    } else {
      car.position.set(spec.fixedX + jitter, 0.12, p0);
      car.rotation.y = spec.speed >= 0 ? 0 : Math.PI;
      cityCars.push({
        group: car,
        axis: 'z',
        pos: car.position.z,
        speed: spec.speed * (0.82 + Math.random() * 0.35),
        min: spec.min,
        max: spec.max,
      });
    }
    cityLifeGroup.add(car);
  }

  for (let i = 0; i < 8; i++) {
    const d = createDroneGroup();
    const baseX = 22 + Math.random() * 95;
    const baseZ = -28 + Math.random() * 78;
    const baseY = 7 + Math.random() * 10;
    d.group.position.set(baseX, baseY, baseZ);
    cityLifeGroup.add(d.group);
    cityDrones.push({
      group: d.group,
      rotors: d.rotors,
      phase: Math.random() * Math.PI * 2,
      baseX,
      baseY,
      baseZ,
    });
  }

  for (let i = 0; i < 6; i++) {
    const pl = createAirplaneGroup();
    const alt = 32 + Math.random() * 14;
    const z = -30 + Math.random() * 82;
    const x = -45 + Math.random() * 65;
    const spd = 24 + Math.random() * 20;
    pl.group.position.set(x, alt, z);
    pl.group.rotation.order = 'YXZ';
    pl.group.rotation.y = (Math.random() - 0.5) * 0.12;
    cityLifeGroup.add(pl.group);
    cityPlanes.push({
      group: pl.group,
      vel: new THREE.Vector3(spd, 0, (Math.random() - 0.5) * 2.2),
      xMin: -70,
      xMax: 210,
    });
  }
}

function updateCityLife(dt) {
  for (const d of cityDrones) {
    d.phase += dt;
    d.group.position.x = d.baseX + Math.cos(d.phase * 0.55) * 1.4;
    d.group.position.z = d.baseZ + Math.sin(d.phase * 0.48) * 1.2;
    d.group.position.y = d.baseY + Math.sin(d.phase * 1.35) * 0.42;
    for (const r of d.rotors) {
      r.rotation.y += dt * 24;
    }
  }
  for (const c of cityCars) {
    c.pos += c.speed * dt;
    if (c.speed >= 0 && c.pos > c.max) c.pos = c.min;
    if (c.speed < 0 && c.pos < c.min) c.pos = c.max;
    if (c.axis === 'x') c.group.position.x = c.pos;
    else c.group.position.z = c.pos;
  }
  for (const p of cityPlanes) {
    p.group.position.addScaledVector(p.vel, dt);
    if (p.group.position.x > p.xMax) p.group.position.x = p.xMin;
    if (p.group.position.x < p.xMin) p.group.position.x = p.xMax;
  }
}

/** @type {THREE.CanvasTexture | null} */
let _facadeTexTemplate = null;

/** Fachada “cerámica / baño” para las torres (tonos blancos y azul agua). */
function getCityBuildingFacadeTexture() {
  if (_facadeTexTemplate) return _facadeTexTemplate;
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0, '#f6fafc');
  g.addColorStop(0.42, '#dceaf4');
  g.addColorStop(1, '#b9d0e8');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 256);
  const cols = 6;
  const rows = 22;
  const cw = 128 / cols;
  const ch = 256 / rows;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const lit = Math.random() > 0.42;
      ctx.fillStyle = lit ? '#bfe0ff' : '#6a8ab0';
      ctx.fillRect(col * cw + 1.5, row * ch + 1.2, cw - 3, ch - 2.4);
      if (lit) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
        ctx.fillRect(col * cw + 2.5, row * ch + 2.2, (cw - 5) * 0.4, (ch - 4) * 0.32);
      }
      ctx.strokeStyle = 'rgba(40, 70, 100, 0.28)';
      ctx.lineWidth = 0.5;
      ctx.strokeRect(col * cw + 1.5, row * ch + 1.2, cw - 3, ch - 2.4);
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  _facadeTexTemplate = tex;
  return tex;
}

function getCityLevelTowerScale() {
  const lv = THREE.MathUtils.clamp(cityGameLevel, 1, CITY_MAX_LEVEL);
  return 1 + (lv - 1) * 0.26;
}

/** Niveles altos: torres más esbeltas (rascacielos). */
function getCityLevelFormNarrow() {
  const lv = THREE.MathUtils.clamp(cityGameLevel, 1, CITY_MAX_LEVEL);
  return Math.max(0.58, 1 - (lv - 1) * 0.041);
}

/**
 * @returns {boolean} si colocó una torre
 */
function tryAddOneTowerInParcel(ix, iz, rx0, rx1, rz0, rz1, stepHint) {
  const mesh = cityBuildingsInstanced;
  if (!mesh || cityBuildingCount >= CONFIG.city.buildingMaxCount) return false;
  const jitterX = (Math.random() - 0.5) * stepHint * 0.44;
  const jitterZ = (Math.random() - 0.5) * stepHint * 0.44;
  const margin = 0.52;
  const px = THREE.MathUtils.clamp((rx0 + rx1) * 0.5 + jitterX, rx0 + margin, rx1 - margin);
  const pz = THREE.MathUtils.clamp((rz0 + rz1) * 0.5 + jitterZ, rz0 + margin, rz1 - margin);

  const narrow = getCityLevelFormNarrow();
  const roll = Math.random();
  const lvScale = getCityLevelTowerScale();
  let w;
  let d;
  let hBase;
  if (roll < 0.4) {
    w = (0.62 + Math.random() * 0.58) * narrow;
    d = (0.58 + Math.random() * 0.54) * narrow;
    hBase = 0.55 + Math.random() * 3.05;
  } else if (roll < 0.66) {
    w = (0.48 + Math.random() * 0.52) * narrow;
    d = (0.48 + Math.random() * 0.5) * narrow;
    hBase = 2.1 + Math.random() * 9.2;
  } else {
    w = (0.4 + Math.random() * 0.46) * narrow;
    d = (0.4 + Math.random() * 0.44) * narrow;
    hBase = 6.5 + Math.random() * 26;
  }
  if (buildingFootprintOnRoad(px, pz, w, d)) return false;

  const h = hBase * lvScale;
  const cy = h * 0.5 + 0.02;
  const bi = cityBuildingCount;
  const o = bi * 3;

  buildingBoxMin[o] = px - w * 0.5;
  buildingBoxMin[o + 1] = 0.02;
  buildingBoxMin[o + 2] = pz - d * 0.5;
  buildingBoxMax[o] = px + w * 0.5;
  buildingBoxMax[o + 1] = h + 0.02;
  buildingBoxMax[o + 2] = pz + d * 0.5;

  buildingParcelIx[bi] = ix;
  buildingParcelIz[bi] = iz;

  _ibuildPos.set(px, cy, pz);
  _ibuildScale.set(w, h, d);
  _ibuildMat.compose(_ibuildPos, _ibuildQuat, _ibuildScale);
  mesh.setMatrixAt(bi, _ibuildMat);

  const colArr = mesh.instanceColor.array;
  const lv = THREE.MathUtils.clamp(cityGameLevel, 1, CITY_MAX_LEVEL);
  const tint = new THREE.Color().setHSL(
    0.54 + Math.random() * 0.14,
    0.05 + (lv - 1) * 0.014,
    0.74 + Math.random() * 0.09 - (lv - 1) * 0.02
  );
  colArr[o] = tint.r;
  colArr[o + 1] = tint.g;
  colArr[o + 2] = tint.b;

  cityBuildingCount++;
  mesh.count = cityBuildingCount;
  const vk = `${ix},${iz}`;
  cityParcelTowerCounts.set(vk, (cityParcelTowerCounts.get(vk) || 0) + 1);
  cityLoadedParcels.add(vk);
  return true;
}

function loadParcelBuildings(ix, iz) {
  if (!CONFIG.city.infiniteParcels) return;
  const vk = `${ix},${iz}`;
  const existing = cityParcelTowerCounts.get(vk) ?? 0;
  if (existing > 0 || cityParcelSkipped.has(vk)) return;
  const S = CONFIG.city.parcelSizeWorld;
  const rx0 = ix * S;
  const rx1 = rx0 + S;
  const rz0 = iz * S;
  const rz1 = rz0 + S;
  const step = Math.max(1.05, S / 6.2);
  let placed = 0;
  const want = CONFIG.city.towersPerParcel;
  const maxTries = want * 16;
  for (let t = 0; t < maxTries && placed < want && cityBuildingCount < CONFIG.city.buildingMaxCount; t++) {
    if (tryAddOneTowerInParcel(ix, iz, rx0, rx1, rz0, rz1, step)) placed++;
  }
  if (placed === 0) cityParcelSkipped.add(vk);
  if (placed > 0) buildingSpatialDirty = true;
}

function unloadParcelBuildings(ix, iz) {
  const toRemove = [];
  for (let bi = 0; bi < cityBuildingCount; bi++) {
    if (buildingParcelIx[bi] === ix && buildingParcelIz[bi] === iz) toRemove.push(bi);
  }
  if (toRemove.length === 0) return;
  toRemove.sort((a, b) => b - a);
  for (const bi of toRemove) removeCityBuildingAtIndex(bi, 'none');
  cityParcelSkipped.delete(`${ix},${iz}`);
  buildingSpatialDirty = true;
}

function syncCityParcelsNow() {
  if (!CONFIG.city.infiniteParcels || gameMode !== 'city' || !cityBuildingsInstanced) return;
  const S = CONFIG.city.parcelSizeWorld;
  const pix = Math.floor(cityPlayerPos.x / S);
  const piz = Math.floor(cityPlayerPos.z / S);
  const L = CONFIG.city.parcelLoadCheb;
  const U = CONFIG.city.parcelUnloadCheb;

  const toUnload = [];
  for (const vk of cityLoadedParcels) {
    const [sx, sz] = vk.split(',').map(Number);
    const dch = Math.max(Math.abs(sx - pix), Math.abs(sz - piz));
    if (dch > U) toUnload.push([sx, sz]);
  }
  for (const [sx, sz] of toUnload) unloadParcelBuildings(sx, sz);

  for (let dx = -L; dx <= L; dx++) {
    for (let dz = -L; dz <= L; dz++) {
      loadParcelBuildings(pix + dx, piz + dz);
    }
  }

  if (buildingSpatialDirty) rebuildBuildingSpatialGrid();
  cityBuildingsInstanced.instanceMatrix.needsUpdate = true;
  cityBuildingsInstanced.instanceColor.needsUpdate = true;
  updateCityHud();
}

function spawnCityBuildings() {
  clearCityBuildings();
  clearCityDebris();
  clearNuclearTerrainMarks();

  ensureBuildingCollisionBuffers();
  const facade = getCityBuildingFacadeTexture();

  const mat = new THREE.MeshStandardMaterial({
    map: facade,
    color: 0xffffff,
    roughness: 0.35,
    metalness: 0.38,
    envMapIntensity: 0.62,
    emissive: new THREE.Color(0xa8c8e8),
    emissiveMap: facade,
    emissiveIntensity: 0.08,
  });

  const geo = new THREE.BoxGeometry(1, 1, 1);
  cityBuildingsInstanced = new THREE.InstancedMesh(geo, mat, CONFIG.city.buildingMaxCount);
  cityBuildingsInstanced.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  cityBuildingsInstanced.instanceColor = new THREE.InstancedBufferAttribute(
    new Float32Array(CONFIG.city.buildingMaxCount * 3),
    3
  );
  cityBuildingsInstanced.instanceColor.setUsage(THREE.DynamicDrawUsage);
  cityBuildingsInstanced.castShadow = true;
  cityBuildingsInstanced.receiveShadow = true;
  cityBuildingsInstanced.frustumCulled = false;
  _ibuildQuat.identity();

  cityBuildingCount = 0;
  cityBuildingsInstanced.count = 0;
  cityBuildingsInstanced.instanceMatrix.needsUpdate = true;
  cityBuildingsInstanced.instanceColor.needsUpdate = true;
  cityRoot.add(cityBuildingsInstanced);

  citySyncParcelAccum = 999;
  syncCityParcelsNow();

  if (!CONFIG.city.infiniteAmmo) {
    cityAmmo = Math.min(
      CONFIG.city.ammoMax,
      Math.max(CONFIG.city.ammo, Math.ceil(cityBuildingCount * 0.1))
    );
  }

  spawnCityLife();
  spawnCityMonsters();
}

function advanceCityLevelAndRespawnMonsters() {
  cityGameLevel = Math.min(cityGameLevel + 1, CITY_MAX_LEVEL);
  clearCityMonsters();
  spawnCityMonsters();
  const supreme = cityGameLevel >= CITY_MAX_LEVEL;
  showCityBanner(
    supreme
      ? cityHeroKey === 'spike'
        ? '¡NIVEL 10 SUPREMO! Speakerman: onda nuclear + bomba ambiental grande al mirar.'
        : '¡NIVEL SUPREMO! Oleada máxima — eres leyenda del baño.'
      : `¡Nivel ${cityGameLevel}! Más monstruos; torres bajas, medianas y rascacielos al explorar.`,
    3200
  );
  updateCityHud();
}

function disposeCityWeapon() {
  if (!cityWeapon) return;
  scene.remove(cityWeapon);
  deepDisposeCityProp(cityWeapon);
  cityWeapon = null;
}

function spawnChainPullDebris(origin, dir) {
  const baseMat = getRubbleSharedMaterial();
  const g = new THREE.Group();
  const n = 14;
  const d = dir.clone();
  if (d.lengthSq() < 1e-8) d.set(0, 0, -1);
  else d.normalize();
  for (let i = 0; i < n; i++) {
    const us = 0.045 + Math.random() * 0.1;
    const geo = new THREE.BoxGeometry(us, us * (0.65 + Math.random() * 0.5), us * 0.5);
    const pieceMat = baseMat.clone();
    pieceMat.color.setRGB(
      0.52 + Math.random() * 0.2,
      0.54 + Math.random() * 0.14,
      0.58 + Math.random() * 0.1
    );
    const m = new THREE.Mesh(geo, pieceMat);
    m.castShadow = true;
    m.position.set((Math.random() - 0.5) * 0.28, (Math.random() - 0.5) * 0.14, (Math.random() - 0.5) * 0.28);
    m.userData.v = new THREE.Vector3(
      d.x * (7 + Math.random() * 7) + (Math.random() - 0.5) * 4,
      4.5 + Math.random() * 11,
      d.z * (7 + Math.random() * 7) + (Math.random() - 0.5) * 4
    );
    m.userData.w = new THREE.Vector3(
      (Math.random() - 0.5) * 12,
      (Math.random() - 0.5) * 12,
      (Math.random() - 0.5) * 12
    );
    g.add(m);
  }
  g.position.copy(origin);
  scene.add(g);
  cityDebrisChunks.push({ group: g, age: 0, maxAge: 1.85 + Math.random() * 0.35 });
}

function spawnExplosionLight(p) {
  const light = new THREE.PointLight(0xff7733, 5.5, 14, 1.8);
  light.position.copy(p);
  scene.add(light);
  cityExplosions.push({ light, t: 0, max: 0.22, baseIntensity: 5.5 });
}

/** Onda expansiva y hongo atómico: solo Speakerman en nivel 10 (SUPREMO). */
function canUseNuclearBombEffects() {
  return cityHeroKey === 'spike' && cityGameLevel >= CITY_MAX_LEVEL;
}

function updateCityAmbientEcoBombs(dt) {
  for (let i = cityAmbientEcoBombs.length - 1; i >= 0; i--) {
    const ab = cityAmbientEcoBombs[i];
    ab.t += dt;
    const k = ab.t / ab.max;
    const fade = Math.max(0, 1 - k);
    if (ab.domeMat) ab.domeMat.opacity = 0.2 * fade;
    if (ab.ringMat) ab.ringMat.opacity = 0.32 * fade;
    if (ab.haloMat) ab.haloMat.opacity = 0.14 * fade;
    ab.group.scale.setScalar(1 + k * 0.42);
    if (ab.t >= ab.max) {
      scene.remove(ab.group);
      deepDisposeCityProp(ab.group);
      cityAmbientEcoBombs.splice(i, 1);
    }
  }
}

/** Bomba ambiental: verde suave, sin sonido ni explosión chillona. */
function spawnAmbientEcoBomb(p, scaleMul = 1) {
  const sm = scaleMul;
  const g = new THREE.Group();
  g.position.set(p.x, p.y, p.z);
  const domeMat = new THREE.MeshBasicMaterial({
    color: 0x3ecf8e,
    transparent: true,
    opacity: 0.2,
    depthWrite: false,
  });
  const dome = new THREE.Mesh(new THREE.SphereGeometry(1.05 * sm, 18, 12), domeMat);
  dome.scale.set(1, 0.4, 1);
  dome.position.y = 0.14 * sm;
  const ringMat = new THREE.MeshBasicMaterial({
    color: 0x7aefb8,
    transparent: true,
    opacity: 0.32,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.12 * sm, 2.75 * sm, 36), ringMat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.02;
  const haloMat = new THREE.MeshBasicMaterial({
    color: 0xb8ffe0,
    transparent: true,
    opacity: 0.14,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const halo = new THREE.Mesh(new THREE.SphereGeometry(0.32 * sm, 10, 8), haloMat);
  halo.position.y = 0.38 * sm;
  g.add(dome, ring, halo);
  scene.add(g);
  cityAmbientEcoBombs.push({
    group: g,
    t: 0,
    max: 2.35 + sm * 0.45,
    domeMat,
    ringMat,
    haloMat,
  });
}

/** Impacto normal: solo bomba ambiental (sin animación de explosión ni audio). */
function spawnConcussionImpact(p) {
  spawnAmbientEcoBomb(p, 1);
}

function spawnStrikeImpactAt(p) {
  if (canUseNuclearBombEffects()) spawnNuclearImpact(p);
  else spawnConcussionImpact(p);
}

/** Nuclear jugable (onda): sin sonido ni hongo animado; bomba ambiental grande. */
function spawnNuclearImpact(p) {
  cityNuclearAfterglow = CONFIG.city.nuclearAfterglowDuration;
  cityNuclearWaves.push({
    origin: p.clone(),
    radius: 0.72,
    prevRadius: 0,
    rMax: CONFIG.city.nuclearWaveMaxRadius,
  });
  spawnAmbientEcoBomb(p, 2.75);
}

const _fireDir = new THREE.Vector3();

/** Impacto nuclear instantáneo hacia donde miras (sin arma ni cohete). */
function cityStrikeAlongView() {
  camera.updateMatrixWorld(true);
  camera.getWorldDirection(_fireDir);
  _fireDir.normalize();
  const maxR = 380;
  _bombP0.copy(camera.position);
  _bombP1.copy(_bombP0).addScaledVector(_fireDir, maxR);
  const pad = CONFIG.city.rocketRadius;
  const hitM = findClosestMonsterOnSegment(_bombP0, _bombP1, pad);
  const hitB = findClosestBuildingOnSegment(_bombP0, _bombP1, pad);
  let hitFirst = null;
  if (hitM && hitB) {
    hitFirst = hitM.u <= hitB.u ? { kind: 'm', ...hitM } : { kind: 'b', ...hitB };
  } else if (hitM) hitFirst = { kind: 'm', ...hitM };
  else if (hitB) hitFirst = { kind: 'b', ...hitB };

  if (hitFirst?.kind === 'm') {
    damageCityMonsterFromRocket(hitFirst.mi, hitFirst.point);
    updateCityHud();
    return;
  }
  if (hitFirst?.kind === 'b') {
    spawnStrikeImpactAt(hitFirst.point);
    removeCityBuildingAtIndex(hitFirst.bi, 'full');
    updateCityHud();
    return;
  }

  const o = _bombP0;
  const d = _fireDir;
  let impactPt = null;
  if (Math.abs(d.y) > 1e-5) {
    const tGr = (0.06 - o.y) / d.y;
    if (tGr > 0.04 && tGr < maxR) impactPt = o.clone().addScaledVector(d, tGr);
  }
  if (!impactPt) impactPt = o.clone().addScaledVector(d, Math.min(42, maxR * 0.12));
  spawnStrikeImpactAt(impactPt);
  updateCityHud();
}

/** @returns {boolean} si hubo impacto */
function tryBeginCityStrike() {
  if (cityFrozen) return false;
  if (!CONFIG.city.infiniteAmmo && cityAmmo <= 0) return false;
  const now = performance.now();
  if (!CONFIG.city.nuclearRapidFire || now - cityLastCannonMs >= 72) {
    cityLastCannonMs = now;
  }
  if (!CONFIG.city.infiniteAmmo) cityAmmo -= 1;
  cityStrikeAlongView();
  if (cityHeroKey === 'camera' && cityGameLevel === 1) {
    camera.getWorldDirection(_fireDir);
    _weaponMuzzleWp.copy(camera.position).addScaledVector(_fireDir, 0.45);
    spawnChainPullDebris(_weaponMuzzleWp.clone(), _fireDir.clone());
  }
  return true;
}

function fireRocket() {
  if (cityFrozen || cityCooldown > 0) return;
  if (!tryBeginCityStrike()) return;
  cityCooldown = CONFIG.city.fireCooldown;
  updateCityHud();
}

function disposeRocket(r) {
  scene.remove(r.group);
  const doneG = new Set();
  const doneM = new Set();
  r.group.traverse((ch) => {
    if (ch.geometry && !doneG.has(ch.geometry)) {
      doneG.add(ch.geometry);
      ch.geometry.dispose();
    }
    const mats = ch.material;
    if (!mats) return;
    const list = Array.isArray(mats) ? mats : [mats];
    for (const mat of list) {
      if (!mat || doneM.has(mat)) continue;
      doneM.add(mat);
      if (mat.map) mat.map.dispose();
      mat.dispose();
    }
  });
}

const _segHitBox = new THREE.Box3();
const _segHitPoint = new THREE.Vector3();

/** @param {THREE.Vector3} p0 @param {THREE.Vector3} p1 @param {THREE.Box3} box @returns {number | null} */
function segmentBoxEnterT(p0, p1, box) {
  const { min, max } = box;
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const dz = p1.z - p0.z;
  let t0 = 0;
  let t1 = 1;
  const eps = 1e-10;

  if (Math.abs(dx) < eps) {
    if (p0.x < min.x || p0.x > max.x) return null;
  } else {
    const inv = 1 / dx;
    let ta = (min.x - p0.x) * inv;
    let tb = (max.x - p0.x) * inv;
    if (ta > tb) {
      const s = ta;
      ta = tb;
      tb = s;
    }
    t0 = Math.max(t0, ta);
    t1 = Math.min(t1, tb);
    if (t0 > t1) return null;
  }

  if (Math.abs(dy) < eps) {
    if (p0.y < min.y || p0.y > max.y) return null;
  } else {
    const inv = 1 / dy;
    let ta = (min.y - p0.y) * inv;
    let tb = (max.y - p0.y) * inv;
    if (ta > tb) {
      const s = ta;
      ta = tb;
      tb = s;
    }
    t0 = Math.max(t0, ta);
    t1 = Math.min(t1, tb);
    if (t0 > t1) return null;
  }

  if (Math.abs(dz) < eps) {
    if (p0.z < min.z || p0.z > max.z) return null;
  } else {
    const inv = 1 / dz;
    let ta = (min.z - p0.z) * inv;
    let tb = (max.z - p0.z) * inv;
    if (ta > tb) {
      const s = ta;
      ta = tb;
      tb = s;
    }
    t0 = Math.max(t0, ta);
    t1 = Math.min(t1, tb);
    if (t0 > t1) return null;
  }

  return t0;
}

function findClosestBuildingOnSegment(p0, p1, pad) {
  if (cityBuildingCount === 0 || p0.distanceToSquared(p1) < 1e-16) return null;
  if (buildingSpatialDirty) {
    rebuildBuildingSpatialGrid();
  }

  const cell = CONFIG.city.buildingSpatialCell;
  const len = Math.sqrt(p0.distanceToSquared(p1));
  const step = Math.max(cell * 0.45, 0.35);
  const steps = Math.max(1, Math.ceil(len / step));

  buildingSegVisitGen++;
  if (buildingSegVisitGen > 0xfffffffe) {
    buildingSegVisitStamp?.fill(0);
    buildingSegVisitGen = 1;
  }
  const stamp = buildingSegVisitGen;
  const visit = buildingSegVisitStamp;

  let bestT = Infinity;
  let bestBi = -1;
  for (let s = 0; s <= steps; s++) {
    const u = s / steps;
    _tmpSegSample.lerpVectors(p0, p1, u);
    const cx = Math.floor(_tmpSegSample.x / cell);
    const cz = Math.floor(_tmpSegSample.z / cell);
    for (let ox = -1; ox <= 1; ox++) {
      for (let oz = -1; oz <= 1; oz++) {
        const list = buildingSpatialGrid.get(`${cx + ox},${cz + oz}`);
        if (!list) continue;
        for (let li = 0; li < list.length; li++) {
          const bi = list[li];
          if (bi >= cityBuildingCount) continue;
          if (visit[bi] === stamp) continue;
          visit[bi] = stamp;
          fillSegHitBoxFromBuildingIndex(bi);
          if (pad > 0) _segHitBox.expandByScalar(pad);
          const t = segmentBoxEnterT(p0, p1, _segHitBox);
          if (t === null || t > 1) continue;
          if (t < bestT) {
            bestT = t;
            bestBi = bi;
          }
        }
      }
    }
  }

  if (bestBi < 0) return null;
  _segHitPoint.lerpVectors(p0, p1, bestT);
  return { bi: bestBi, point: _segHitPoint.clone(), u: bestT };
}

const _segSphereD = new THREE.Vector3();
const _segSphereF = new THREE.Vector3();
const _monHitCenter = new THREE.Vector3();

/**
 * @param {THREE.Vector3} p0
 * @param {THREE.Vector3} p1
 * @param {THREE.Vector3} center
 * @param {number} radius
 * @returns {number | null} t in [0,1] along segment p0→p1
 */
function segmentSphereEnterT(p0, p1, center, radius) {
  _segSphereD.subVectors(p1, p0);
  _segSphereF.subVectors(p0, center);
  const a = _segSphereD.lengthSq();
  const b = 2 * _segSphereF.dot(_segSphereD);
  const c = _segSphereF.lengthSq() - radius * radius;
  if (a < 1e-14) return c <= 0 ? 0 : null;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const sqrtD = Math.sqrt(disc);
  let t = (-b - sqrtD) / (2 * a);
  if (t < 0 || t > 1) t = (-b + sqrtD) / (2 * a);
  if (t < 0 || t > 1) return null;
  return t;
}

function findClosestMonsterOnSegment(p0, p1, pad) {
  if (cityMonsters.length === 0 || p0.distanceToSquared(p1) < 1e-16) return null;
  const r = CONFIG.city.monsterHitRadius + pad;
  let bestT = Infinity;
  let bestMi = -1;
  for (let mi = 0; mi < cityMonsters.length; mi++) {
    const m = cityMonsters[mi];
    m.group.updateMatrixWorld(true);
    _monHitCenter.copy(m.group.position);
    _monHitCenter.y += CONFIG.city.monsterHitCenterY;
    const t = segmentSphereEnterT(p0, p1, _monHitCenter, r);
    if (t !== null && t < bestT) {
      bestT = t;
      bestMi = mi;
    }
  }
  if (bestMi < 0) return null;
  _segHitPoint.lerpVectors(p0, p1, bestT);
  return { mi: bestMi, point: _segHitPoint.clone(), u: bestT };
}

/**
 * @param {THREE.Box3} box
 * @param {THREE.Vector3} center
 * @param {number} radius
 */
function box3IntersectsSphere(box, center, radius) {
  const cx = THREE.MathUtils.clamp(center.x, box.min.x, box.max.x);
  const cy = THREE.MathUtils.clamp(center.y, box.min.y, box.max.y);
  const cz = THREE.MathUtils.clamp(center.z, box.min.z, box.max.z);
  const dx = center.x - cx;
  const dy = center.y - cy;
  const dz = center.z - cz;
  const r2 = radius * radius;
  return dx * dx + dy * dy + dz * dz <= r2;
}

/**
 * True if the AABB intersects the closed spherical shell [rInner, rOuter] around center.
 * @param {THREE.Box3} box
 * @param {THREE.Vector3} center
 */
function box3IntersectsSphericalShell(box, center, rInner, rOuter) {
  const ox = center.x;
  const oy = center.y;
  const oz = center.z;
  const cx = THREE.MathUtils.clamp(ox, box.min.x, box.max.x);
  const cy = THREE.MathUtils.clamp(oy, box.min.y, box.max.y);
  const cz = THREE.MathUtils.clamp(oz, box.min.z, box.max.z);
  const dx0 = ox - cx;
  const dy0 = oy - cy;
  const dz0 = oz - cz;
  const dCloseSq = dx0 * dx0 + dy0 * dy0 + dz0 * dz0;
  const rOut2 = rOuter * rOuter;
  if (dCloseSq > rOut2) return false;
  let dFarSq = 0;
  for (const xb of [box.min.x, box.max.x]) {
    for (const yb of [box.min.y, box.max.y]) {
      for (const zb of [box.min.z, box.max.z]) {
        const dx = xb - ox;
        const dy = yb - oy;
        const dz = zb - oz;
        dFarSq = Math.max(dFarSq, dx * dx + dy * dy + dz * dz);
      }
    }
  }
  const rIn2 = rInner * rInner;
  return dFarSq >= rIn2;
}

/** Point vs spherical shell [rInner, rOuter]. */
function pointInSphericalShell(p, center, rInner, rOuter) {
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  const dz = p.z - center.z;
  const d2 = dx * dx + dy * dy + dz * dz;
  return d2 <= rOuter * rOuter && d2 >= rInner * rInner;
}

function spawnNuclearCraterRing(p) {
  if (xzAabbIntersectsAnyRoad(p.x - 0.4, p.x + 0.4, p.z - 0.4, p.z + 0.4)) return;
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.35, 4.5, 42),
    new THREE.MeshStandardMaterial({
      color: 0x151210,
      roughness: 0.97,
      metalness: 0,
      side: THREE.DoubleSide,
    })
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.set(p.x, 0.011, p.z);
  ring.receiveShadow = true;
  cityEnvironmentGroup.add(ring);
  cityCraterRings.push(ring);
}

function updateNuclearWaves(dtSim) {
  if (cityNuclearWaves.length === 0) return;
  let hudDirty = false;
  for (let wi = cityNuclearWaves.length - 1; wi >= 0; wi--) {
    const w = cityNuclearWaves[wi];
    w.radius += CONFIG.city.nuclearWaveSpeed * dtSim;
    const r = w.radius;
    const o = w.origin;
    const r2 = r * r;

    _waveBuildingHitScratch.length = 0;
    for (let bi = 0; bi < cityBuildingCount; bi++) {
      fillSegHitBoxFromBuildingIndex(bi);
      if (box3IntersectsSphere(_segHitBox, o, r)) _waveBuildingHitScratch.push(bi);
    }
    if (_waveBuildingHitScratch.length > 0) {
      _waveBuildingHitScratch.sort((a, b) => b - a);
      for (const bi of _waveBuildingHitScratch) removeCityBuildingAtIndex(bi, 'light');
      hudDirty = true;
    }

    for (let mi = cityMonsters.length - 1; mi >= 0; mi--) {
      const mon = cityMonsters[mi];
      mon.group.updateMatrixWorld(true);
      _monHitCenter.copy(mon.group.position);
      _monHitCenter.y += CONFIG.city.monsterHitCenterY;
      if (_monHitCenter.distanceToSquared(o) > r2) continue;
      cityMonsterGroup.remove(mon.group);
      deepDisposeCityProp(mon.group);
      cityMonsters.splice(mi, 1);
      hudDirty = true;
    }

    for (let li = cityLifeGroup.children.length - 1; li >= 0; li--) {
      const ob = cityLifeGroup.children[li];
      ob.updateMatrixWorld(true);
      ob.getWorldPosition(_nuclearBlastWp);
      if (_nuclearBlastWp.distanceToSquared(o) > r2) continue;
      cityLifeGroup.remove(ob);
      deepDisposeCityProp(ob);
      cityCars = cityCars.filter((c) => c.group !== ob);
      cityDrones = cityDrones.filter((d) => d.group !== ob);
      cityPlanes = cityPlanes.filter((pl) => pl.group !== ob);
    }

    if (w.radius >= w.rMax) cityNuclearWaves.splice(wi, 1);
  }
  if (hudDirty) updateCityHud();
}

function refreshScoreEls() {
  elP1 = document.getElementById('p1');
  elP2 = document.getElementById('p2');
  elCityHp = document.getElementById('city-hp');
  elCityMon = document.getElementById('city-mon');
  elCityLevel = document.getElementById('city-level');
  elCityHero = document.getElementById('city-hero');
}

function resetPongScores() {
  elScore.innerHTML = '<span id="p1">0</span> — <span id="p2">0</span>';
  refreshScoreEls();
}

let scoreP1 = 0;
let scoreP2 = 0;

function resetBallPong(direction) {
  ballPos.set(0, 0);
  const r = getPongBallRadius();
  ball.position.set(0, r + 0.001, 0);
  const angle = (Math.random() - 0.5) * 0.55;
  const dirSign = direction >= 0 ? 1 : -1;
  const spdMin = CONFIG.baseSpeed * CONFIG.ballSpeedMinMul;
  let spd = getEffectiveBaseSpeed();
  if (!Number.isFinite(spd) || spd < spdMin) spd = spdMin;
  ballVel.set(dirSign * spd * Math.cos(angle), spd * Math.sin(angle));
  const len = ballVel.length();
  if (len < 1e-6) ballVel.set(dirSign * spd, 0);
  else ballVel.multiplyScalar(spd / len);
}

function scorePong(who) {
  const prevTotal = scoreP1 + scoreP2;
  if (who === 1) {
    scoreP1 += 1;
    elP1.textContent = String(scoreP1);
  } else {
    scoreP2 += 1;
    elP2.textContent = String(scoreP2);
  }
  const total = scoreP1 + scoreP2;
  if (pongSandbox) {
    const prevTier = Math.floor(prevTotal / CONFIG.pongSandboxGrowEvery);
    const newTier = Math.floor(total / CONFIG.pongSandboxGrowEvery);
    if (newTier > prevTier) {
      pongBallRadiusMul = Math.min(
        CONFIG.pongSandboxMaxRadiusMul,
        pongBallRadiusMul * CONFIG.pongSandboxGrowFactor
      );
      syncPongBallVisual();
    }
  } else if (scoreP1 >= CONFIG.winScore || scoreP2 >= CONFIG.winScore) {
    scoreP1 = 0;
    scoreP2 = 0;
    elP1.textContent = '0';
    elP2.textContent = '0';
  }
  resetBallPong(who === 1 ? 1 : -1);
}

function setPongVisibility(on) {
  table.visible = on;
  centerLine.visible = on && vsRobot;
  edgeT.visible = on;
  edgeB.visible = on;
  pongCourtRing.visible = on;
  pongCourtRingOuter.visible = on;
  playerPaddle.visible = on;
  aiPaddle.visible = on;
  ball.visible = on;
  cityEnvironmentGroup.visible = !on && gameMode === 'city';
  cityRoot.visible = !on && gameMode === 'city';
  cityLifeGroup.visible = !on && gameMode === 'city';
  cityMonsterGroup.visible = !on && gameMode === 'city';
}

function updateCityHud() {
  if (elCityLevel) {
    elCityLevel.textContent =
      cityGameLevel >= CITY_MAX_LEVEL ? 'SUPREMO' : `Nv ${cityGameLevel}`;
  }
  if (elCityHero) elCityHero.textContent = getCityHeroLabel();
  elP1.textContent = String(cityBuildingCount);
  elP2.textContent = CONFIG.city.infiniteAmmo ? '∞' : String(cityAmmo);
  if (elCityHp) elCityHp.textContent = String(Math.max(0, Math.ceil(cityPlayerHp)));
  if (elCityMon) elCityMon.textContent = String(cityMonsters.length);
}

function removeCityMonsterAt(mi) {
  const m = cityMonsters[mi];
  if (!m) return;
  cityMonsterGroup.remove(m.group);
  deepDisposeCityProp(m.group);
  cityMonsters.splice(mi, 1);
  updateCityHud();
}

function applyCityPlayerDamage(amount) {
  if (gameMode !== 'city' || cityFrozen) return;
  cityPlayerHp -= amount;
  cityPlayerHp = Math.max(0, cityPlayerHp);
  updateCityHud();
  if (cityPlayerHp <= 0) {
    showCityBanner('Radiación letal. Menú para reintentar.', 4000);
    cityFrozen = true;
    cityLose = true;
  }
}

function damageCityMonsterFromRocket(mi, hitPoint) {
  const m = cityMonsters[mi];
  if (!m) return;
  const now = performance.now();
  const frozen = m.frozenUntil && now < m.frozenUntil;
  if (!frozen) applyCityPlayerDamage(CONFIG.city.monsterPlayerSelfDamage);
  m.hp -= CONFIG.city.rocketDamageToMonster * getHeroRocketDamageMul();
  spawnStrikeImpactAt(hitPoint);
  if (m.hp <= 0) {
    removeCityMonsterAt(mi);
  } else {
    updateCityHud();
  }
}

function applyMonsterFreezeVisual(mon, frozen) {
  mon.group.traverse((ch) => {
    if (!ch.isMesh) return;
    const mat = ch.material;
    if (!mat || !mat.emissive) return;
    if (!ch.userData._freezeEm) ch.userData._freezeEm = mat.emissive.clone();
    if (frozen) mat.emissive.setHex(0x66ccff);
    else mat.emissive.copy(ch.userData._freezeEm);
  });
}

function updateMonsterFreezeStates() {
  const now = performance.now();
  for (const m of cityMonsters) {
    const fr = m.frozenUntil && now < m.frozenUntil;
    if (fr !== m._freezeShown) {
      m._freezeShown = fr;
      applyMonsterFreezeVisual(m, !!fr);
    }
  }
}

function tryTvmanParalyze() {
  if (cityHeroKey !== 'titan' || cityFrozen) return;
  if (cityTvParalyzeCd > 0) return;
  cityTvParalyzeCd = 8;
  resumeAudio();
  playTvParalyzeHum();
  const until = performance.now() + 6200;
  for (const m of cityMonsters) {
    m.frozenUntil = until;
  }
  showCityBanner(
    '¡Paralización! Pantalla azul: no te quitan PV al dispararles mientras dura.',
    2800
  );
  updateMonsterFreezeStates();
}

function tryTvmanBurnEyes() {
  if (cityHeroKey !== 'titan' || cityFrozen) return;
  if (cityGameLevel < CITY_MAX_LEVEL) return;
  if (cityTvBurnCd > 0) return;
  cityTvBurnCd = 14;
  resumeAudio();
  playTvBurnGlitch();
  applyCityPlayerDamage(8);
  for (let mi = cityMonsters.length - 1; mi >= 0; mi--) {
    const m = cityMonsters[mi];
    m.hp -= 95;
    if (m.hp <= 0) removeCityMonsterAt(mi);
  }
  showCityBanner('¡Quemadura de pantalla! Daño masivo a monstruos (te cuesta un poco de PV).', 2400);
  updateCityHud();
}

function showCityBanner(text, ms) {
  if (!elCityBanner) return;
  elCityBanner.textContent = text;
  elCityBanner.classList.add('show');
  cityBannerTimer = ms;
}

function restorePongCamera() {
  camera.fov = 50;
  camera.updateProjectionMatrix();
  camera.position.copy(CONFIG.pongCameraPos);
  camera.rotation.order = 'XYZ';
  camera.rotation.set(0, 0, 0);
  camera.lookAt(0, 0.05, 0);
  scene.fog = new THREE.Fog(0xf5c4b8, 28, 92);
}

function openMenu() {
  document.exitPointerLock?.();
  gameMode = 'menu';
  heroOverlay?.classList.add('hidden');
  cityFrozen = false;
  cityLose = false;
  clearCityBuildings();
  clearCityLife();
  clearCityMonsters();
  clearCityRockets();
  clearCityExplosions();
  clearNuclearTerrainMarks();
  clearCityDebris();
  cityCooldown = 0;
  cityNuclearAfterglow = 0;
  cityFireHeld = false;
  cityMonsterWavePendingRespawn = false;
  cityTvParalyzeCd = 0;
  cityTvBurnCd = 0;
  elCityBanner?.classList.remove('show');
  elCrosshair?.classList.remove('on');
  menuOverlay.classList.remove('hidden');
  elBallSpeedWrap.classList.add('hidden');
  document.getElementById('speed-btns')?.classList.add('hidden');
  touchPlayerPointerId = null;
  touchAiPointerId = null;
  touchPlayerTargetZ = null;
  touchAiTargetZ = null;
  if (isTouchDevice) {
    const hint = document.querySelector('#menu-overlay .menu-hint');
    if (hint) {
      hint.innerHTML =
        'Pong clásico. <strong>Arrastra el dedo</strong> para mover la paleta. Botones <strong>+/−</strong> para la velocidad de la pelota.';
    }
  }
  setPongVisibility(true);
  centerLine.visible = true;
  cityEnvironmentGroup.visible = false;
  cityRoot.visible = false;
  cityLifeGroup.visible = false;
  cityMonsterGroup.visible = false;
  ball.visible = true;
  restorePongCamera();
  elHelpInner.innerHTML = 'Elige un modo en el menú.';
  resetPongScores();
  scoreP1 = 0;
  scoreP2 = 0;
  pongSandbox = false;
  pongBallRadiusMul = 1;
  syncPongBallVisual();
}

/**
 * @param {boolean} ai
 * @param {boolean} [sandbox] Partida infinita; cada 10 goles (suma) la pelota crece.
 */
function startPong(ai, sandbox = false) {
  resumeAudio();
  vsRobot = ai;
  pongSandbox = sandbox;
  pongBallRadiusMul = 1;
  syncPongBallVisual();
  if (!Number.isFinite(ballSpeedMul) || ballSpeedMul < CONFIG.ballSpeedMinMul) {
    ballSpeedMul = CONFIG.ballSpeedMinMul;
  }
  gameMode = 'pong';
  restorePongCamera();
  menuOverlay.classList.add('hidden');
  elBallSpeedWrap.classList.remove('hidden');
  document.getElementById('speed-btns')?.classList.remove('hidden');
  const bslPong = document.getElementById('ball-speed-label');
  if (bslPong) bslPong.textContent = 'Vel. bola';
  setPongVisibility(true);
  centerLine.visible = ai;
  cityEnvironmentGroup.visible = false;
  cityRoot.visible = false;
  cityLifeGroup.visible = false;
  resetPongScores();
  scoreP1 = 0;
  scoreP2 = 0;
  resetBallPong(1);
  if (elBallSpeed) elBallSpeed.textContent = `${Math.round(ballSpeedMul * 100)}%`;
  if (isTouchDevice) {
    if (sandbox) {
      elHelpInner.innerHTML = ai
        ? 'Sandbox: <strong>arrastra el dedo</strong> para mover tu paleta · botones <strong>+/−</strong> para velocidad · cada 10 goles la pelota crece · <strong>Menú</strong>'
        : 'Sandbox 2 jug: <strong>arrastra</strong> en tu mitad (azul izq, rojo dcha) · botones <strong>+/−</strong> · cada 10 goles la pelota crece · <strong>Menú</strong>';
    } else if (ai) {
      elHelpInner.innerHTML =
        '<strong>Arrastra el dedo</strong> para mover tu paleta · botones <strong>+/−</strong> para velocidad · A 11 puntos · <strong>Menú</strong>';
    } else {
      elHelpInner.innerHTML =
        '2 jugadores: cada uno <strong>arrastra</strong> en su mitad (azul izquierda, rojo derecha) · <strong>+/−</strong> velocidad · A 11 · <strong>Menú</strong>';
    }
  } else if (sandbox) {
    if (ai) {
      elHelpInner.innerHTML =
        'Sandbox: <strong>sin límite</strong> de puntos · cada <strong>10 goles</strong> (suma de los dos) la <strong>pelota crece</strong>. Azul: <kbd>W</kbd> <kbd>S</kbd> · <kbd>P</kbd> / <kbd>R</kbd> · <kbd>Menú</kbd>';
    } else {
      elHelpInner.innerHTML =
        'Sandbox 2 jugadores: partida infinita; cada <strong>10 goles</strong> la pelota crece. Azul <kbd>W</kbd><kbd>S</kbd> · Rojo <kbd>↑</kbd><kbd>↓</kbd> · <kbd>P</kbd> / <kbd>R</kbd> · <kbd>Menú</kbd>';
    }
  } else if (ai) {
    elHelpInner.innerHTML =
      'Azul: <kbd>W</kbd> <kbd>S</kbd> · Velocidad: <kbd>P</kbd> / <kbd>R</kbd> · A 11 puntos · <kbd>Menú</kbd>';
  } else {
    elHelpInner.innerHTML =
      'Azul: <kbd>W</kbd> <kbd>S</kbd> · Rojo: <kbd>↑</kbd> <kbd>↓</kbd> · <kbd>P</kbd> / <kbd>R</kbd> · A 11 · <kbd>Menú</kbd>';
  }
}

function startCity() {
  resumeAudio();
  gameMode = 'city';
  cityGameLevel = 1;
  disposeCityWeapon();
  cityTvParalyzeCd = 0;
  cityTvBurnCd = 0;
  menuOverlay.classList.add('hidden');
  elBallSpeedWrap.classList.remove('hidden');
  const bslCity = document.getElementById('ball-speed-label');
  if (bslCity) bslCity.textContent = 'Potencia de impacto (mirada)';
  table.visible = false;
  centerLine.visible = false;
  edgeT.visible = false;
  edgeB.visible = false;
  pongCourtRing.visible = false;
  pongCourtRingOuter.visible = false;
  aiPaddle.visible = false;
  playerPaddle.visible = false;
  ball.visible = false;
  cityEnvironmentGroup.visible = true;
  cityRoot.visible = true;
  cityLifeGroup.visible = true;
  cityAmmo = CONFIG.city.ammo;
  cityYaw = 0;
  cityPitch = 0;
  cityPlayerPos.copy(CITY_SPAWN);
  cityVertVel = 0;
  cityLastSpaceDownMs = 0;
  cityFlyThrustCooldownFrames = 0;
  cityCooldown = 0;
  cityNuclearAfterglow = 0;
  cityFireHeld = false;
  cityPlayerHp = CONFIG.city.playerMaxHp;
  cityMonsterWavePendingRespawn = false;
  cityFrozen = false;
  cityLose = false;
  clearCityRockets();
  clearCityExplosions();
  elCityBanner?.classList.remove('show');
  elCrosshair?.classList.add('on');
  camera.fov = 58;
  camera.updateProjectionMatrix();
  scene.fog = new THREE.Fog(0xffc8b8, 38, 168);
  spawnCityBuildings();
  elScore.innerHTML =
    '<span id="city-level">Nv 1</span> · <span id="p1">0</span> torres · <span id="p2">0</span> energía · ♥ <span id="city-hp">0</span> · monstruos <span id="city-mon">0</span> · <span id="city-hero">—</span>';
  refreshScoreEls();
  updateCityHud();
  if (elBallSpeed) elBallSpeed.textContent = `${Math.round(ballSpeedMul * 100)}%`;
  elHelpInner.innerHTML =
    'Sin armas: <strong>clic</strong> = impacto silencioso donde miras; ves la <strong>bomba ambiental</strong> (verde). <strong>Onda nuclear</strong> de juego solo <strong>Speakerman nivel 10</strong> (también verde, sin explosión ruidosa). Más torres pequeñas y grandes. <strong>Camaramán</strong> nv.1: cascada; nv.2+: jetpack. <strong>Tibimán</strong>: <kbd>Q</kbd> / <kbd>E</kbd>. <kbd>WASD</kbd> · <kbd>Espacio</kbd> · <kbd>Menú</kbd>';
}

/**
 * Delegación a nivel documento: en iOS Safari el `click` se suprime a veces
 * tras un :active aunque el botón sí registre el toque (cyan glow se ve, pero
 * el handler no corre). Escuchamos `click` + `pointerup` (capture) sobre todo
 * el documento, con un dedupe único de 400 ms para evitar doble disparo
 * cuando ambos eventos sí llegan al mismo botón.
 */
const _btnDelegateActions = {
  'btn-pong-ai': () => startPong(true),
  'btn-pong-2p': () => startPong(false),
  'btn-city': () => openHeroPick(),
  'to-menu': () => openMenu(),
  'btn-speed-down': () => {
    if (gameMode === 'menu') return;
    resumeAudio();
    setBallSpeedMul(ballSpeedMul - CONFIG.ballSpeedStep);
  },
  'btn-speed-up': () => {
    if (gameMode === 'menu') return;
    resumeAudio();
    setBallSpeedMul(ballSpeedMul + CONFIG.ballSpeedStep);
  },
  'btn-hero-start': () => confirmHeroAndStartCity(),
  'btn-hero-cam': () => setHeroAndStart('camera'),
  'btn-hero-spike': () => setHeroAndStart('spike'),
  'btn-hero-titan': () => setHeroAndStart('titan'),
  'btn-hero-back': () => {
    heroOverlay?.classList.add('hidden');
    menuOverlay.classList.remove('hidden');
  },
};
let _btnDelegateLast = 0;
function _btnDelegateFire(id) {
  const fn = _btnDelegateActions[id];
  if (!fn) return false;
  const now = Date.now();
  if (now - _btnDelegateLast < 400) return true;
  _btnDelegateLast = now;
  fn();
  return true;
}
document.addEventListener('click', (e) => {
  const btn = e.target?.closest?.('button');
  if (btn?.id) _btnDelegateFire(btn.id);
}, true);
document.addEventListener('pointerup', (e) => {
  if (e.pointerType === 'mouse') return;
  if (e.button !== undefined && e.button !== 0) return;
  const btn = e.target?.closest?.('button');
  if (btn?.id) _btnDelegateFire(btn.id);
}, true);

heroNameInput?.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    confirmHeroAndStartCity();
  }
});

window.addEventListener(
  'keydown',
  (e) => {
    if (gameMode !== 'city' || cityFrozen) return;
    if (e.repeat) return;
    if (e.code === 'KeyQ' && cityHeroKey === 'titan') {
      e.preventDefault();
      tryTvmanParalyze();
    }
    if (e.code === 'KeyE' && cityHeroKey === 'titan' && cityGameLevel >= CITY_MAX_LEVEL) {
      e.preventDefault();
      tryTvmanBurnEyes();
    }
  },
  true
);

const tmpV2 = new THREE.Vector2();
const ballSpin = new THREE.Vector3();

function updatePong(dt) {
  if (
    !Number.isFinite(ballVel.x) ||
    !Number.isFinite(ballVel.y) ||
    ballVel.lengthSq() < 1e-12
  ) {
    resetBallPong(Math.random() < 0.5 ? 1 : -1);
  }

  const paddleMin = -halfY + CONFIG.paddle.halfY + 0.05;
  const paddleMax = halfY - CONFIG.paddle.halfY - 0.05;
  const touchMaxStep = CONFIG.playerMove * dt * 1.7;

  const p1 = (keys.KeyW ? 1 : 0) - (keys.KeyS ? 1 : 0);
  playerPaddle.position.z = THREE.MathUtils.clamp(
    playerPaddle.position.z + p1 * CONFIG.playerMove * dt,
    paddleMin,
    paddleMax
  );
  if (touchPlayerTargetZ != null) {
    const cur = playerPaddle.position.z;
    const diff = touchPlayerTargetZ - cur;
    const step = Math.sign(diff) * Math.min(Math.abs(diff), touchMaxStep);
    playerPaddle.position.z = THREE.MathUtils.clamp(cur + step, paddleMin, paddleMax);
  }

  if (vsRobot) {
    const targetZ = ballPos.y;
    let aiZ = aiPaddle.position.z;
    const diff = targetZ - aiZ;
    if (Math.abs(diff) > CONFIG.aiDeadzone) {
      aiZ += Math.sign(diff) * Math.min(Math.abs(diff), CONFIG.aiSpeed * dt);
    }
    aiPaddle.position.z = THREE.MathUtils.clamp(aiZ, paddleMin, paddleMax);
  } else {
    const p2 = (keys.ArrowUp ? 1 : 0) - (keys.ArrowDown ? 1 : 0);
    aiPaddle.position.z = THREE.MathUtils.clamp(
      aiPaddle.position.z + p2 * CONFIG.playerMove * dt,
      paddleMin,
      paddleMax
    );
    if (touchAiTargetZ != null) {
      const cur = aiPaddle.position.z;
      const diff = touchAiTargetZ - cur;
      const step = Math.sign(diff) * Math.min(Math.abs(diff), touchMaxStep);
      aiPaddle.position.z = THREE.MathUtils.clamp(cur + step, paddleMin, paddleMax);
    }
  }

  ballPos.add(tmpV2.copy(ballVel).multiplyScalar(dt));

  const br = getPongBallRadius();
  if (ballPos.y > halfY - br) {
    ballPos.y = halfY - br;
    ballVel.y *= -1;
    resumeAudio();
    playBallWallBounce();
  } else if (ballPos.y < -halfY + br) {
    ballPos.y = -halfY + br;
    ballVel.y *= -1;
    resumeAudio();
    playBallWallBounce();
  }

  const px = -paddleX;
  const ax = paddleX;

  if (ballVel.x < 0 && ballPos.x - br < px + CONFIG.paddle.halfX) {
    if (
      ballPos.y <= playerPaddle.position.z + CONFIG.paddle.halfY + br * 0.85 &&
      ballPos.y >= playerPaddle.position.z - CONFIG.paddle.halfY - br * 0.85
    ) {
      ballPos.x = px + CONFIG.paddle.halfX + br + 0.01;
      const rel = (ballPos.y - playerPaddle.position.z) / (CONFIG.paddle.halfY + br);
      ballVel.x = Math.abs(ballVel.x);
      ballVel.y += rel * 4.6;
      let sp = Math.min(ballVel.length() * CONFIG.hitSpeedMul, getEffectiveMaxSpeed());
      ballVel.normalize().multiplyScalar(sp);
      resumeAudio();
      playBallPaddleHit(sp);
    }
  }

  if (ballVel.x > 0 && ballPos.x + br > ax - CONFIG.paddle.halfX) {
    if (
      ballPos.y <= aiPaddle.position.z + CONFIG.paddle.halfY + br * 0.85 &&
      ballPos.y >= aiPaddle.position.z - CONFIG.paddle.halfY - br * 0.85
    ) {
      ballPos.x = ax - CONFIG.paddle.halfX - br - 0.01;
      const rel = (ballPos.y - aiPaddle.position.z) / (CONFIG.paddle.halfY + br);
      ballVel.x = -Math.abs(ballVel.x);
      ballVel.y += rel * 4.6;
      let sp = Math.min(ballVel.length() * CONFIG.hitSpeedMul, getEffectiveMaxSpeed());
      ballVel.normalize().multiplyScalar(sp);
      resumeAudio();
      playBallPaddleHit(sp);
    }
  }

  if (ballPos.x < -halfX - br * 2) {
    scorePong(2);
  } else if (ballPos.x > halfX + br * 2) {
    scorePong(1);
  }

  ball.position.x = ballPos.x;
  ball.position.z = ballPos.y;
  ball.position.y = br + 0.001;

  const vlen = ballVel.length();
  if (vlen > 1e-6) {
    ballSpin.set(-ballVel.y, 0, ballVel.x).normalize();
    const spinSpeed = vlen * 3.4;
    if (ballSpin.lengthSq() > 1e-6) ball.rotateOnWorldAxis(ballSpin, spinSpeed * dt);
  }
}

function updateCity(dt) {
  if (cityFrozen) {
    cityNuclearAfterglow = Math.max(0, cityNuclearAfterglow - dt);
    if (cityNuclearWaves.length > 0) {
      updateNuclearWaves(dt * CONFIG.city.slowMoScale);
    }
    updateCityDebris(dt);
    updateCityAmbientEcoBombs(dt);
    cityBannerTimer -= dt * 1000;
    if (cityBannerTimer <= 0) {
      elCityBanner?.classList.remove('show');
      if (cityLose) {
        cityLose = false;
        openMenu();
        return;
      }
      if (cityMonsterWavePendingRespawn) {
        cityMonsterWavePendingRespawn = false;
        advanceCityLevelAndRespawnMonsters();
      } else if (cityBuildingCount === 0) {
        citySyncParcelAccum = 999;
        syncCityParcelsNow();
        updateCityHud();
      }
      cityFrozen = false;
    }
    return;
  }

  cityCooldown -= dt;

  cityTvParalyzeCd = Math.max(0, cityTvParalyzeCd - dt);
  cityTvBurnCd = Math.max(0, cityTvBurnCd - dt);
  updateMonsterFreezeStates();

  citySyncParcelAccum += dt;
  if (citySyncParcelAccum >= 0.14) {
    citySyncParcelAccum = 0;
    syncCityParcelsNow();
  }

  if (
    CONFIG.city.nuclearRapidFire &&
    cityFireHeld &&
    document.pointerLockElement === renderer.domElement &&
    !cityFrozen
  ) {
    const cap = CONFIG.city.nuclearShotsPerSecondCap;
    const per = 1 / cap;
    let burst = 0;
    const maxB = Math.min(120, Math.ceil(cap * dt) + 10);
    while (cityCooldown <= 0 && burst < maxB) {
      if (!CONFIG.city.infiniteAmmo && cityAmmo <= 0) break;
      if (!tryBeginCityStrike()) break;
      cityCooldown += per;
      burst++;
    }
    if (burst > 0) updateCityHud();
  } else {
    cityCooldown = Math.max(0, cityCooldown);
  }
  const slowMoOn =
    cityRockets.length > 0 || cityNuclearAfterglow > 0 || cityNuclearWaves.length > 0;
  const dtSim = slowMoOn ? dt * CONFIG.city.slowMoScale : dt;
  cityNuclearAfterglow = Math.max(0, cityNuclearAfterglow - dt);

  const ms = CONFIG.city.moveSpeed * dt;
  _cityYawOnly.rotation.order = 'YXZ';
  _cityYawOnly.rotation.x = 0;
  _cityYawOnly.rotation.y = cityYaw;
  _cityYawOnly.rotation.z = 0;
  _cityYawOnly.updateMatrixWorld(true);
  _cityYawOnly.getWorldDirection(_cityFwdFlat);
  _cityFwdFlat.y = 0;
  if (_cityFwdFlat.lengthSq() > 1e-8) _cityFwdFlat.normalize();
  _cityRightFlat.crossVectors(_cityFwdFlat, _worldUp).normalize();

  if (keys.KeyW) cityPlayerPos.addScaledVector(_cityFwdFlat, -ms);
  if (keys.KeyS) cityPlayerPos.addScaledVector(_cityFwdFlat, ms);
  if (keys.KeyA) cityPlayerPos.addScaledVector(_cityRightFlat, ms);
  if (keys.KeyD) cityPlayerPos.addScaledVector(_cityRightFlat, -ms);

  if (cityFlyThrustCooldownFrames > 0) {
    cityFlyThrustCooldownFrames--;
  } else if (keys.Space) {
    let thrustMul = 1;
    if (cityHeroKey === 'camera' && cityGameLevel >= 2) thrustMul = 1.65;
    cityVertVel += CONFIG.city.flyThrust * thrustMul * dt;
  }
  cityVertVel -= CONFIG.city.flyGravity * dt;
  cityVertVel = THREE.MathUtils.clamp(cityVertVel, -CONFIG.city.flyMaxVertSpeed, CONFIG.city.flyMaxVertSpeed);

  cityPlayerPos.y += cityVertVel * dt;
  cityPlayerPos.y = THREE.MathUtils.clamp(cityPlayerPos.y, CITY_GROUND_Y, CONFIG.city.flyMaxY);
  if (cityPlayerPos.y <= CITY_GROUND_Y + 1e-4 && cityVertVel < 0) {
    cityPlayerPos.y = CITY_GROUND_Y;
    cityVertVel = 0;
  }

  const wbx = CONFIG.city.worldBoundX ?? 200;
  const wbz = CONFIG.city.worldBoundZ ?? 200;
  cityPlayerPos.x = THREE.MathUtils.clamp(cityPlayerPos.x, -wbx, wbx);
  cityPlayerPos.z = THREE.MathUtils.clamp(cityPlayerPos.z, -wbz, wbz);

  camera.position.copy(cityPlayerPos);
  camera.rotation.order = 'YXZ';
  camera.rotation.y = cityYaw;
  camera.rotation.x = cityPitch;

  updateCityLife(dtSim);
  updateNuclearWaves(dtSim);
  updateCityDebris(dtSim);
  updateCityAmbientEcoBombs(dtSim);

  for (let i = cityExplosions.length - 1; i >= 0; i--) {
    const ex = cityExplosions[i];
    ex.t += dtSim;
    const bi = ex.baseIntensity ?? 5.5;
    ex.light.intensity = bi * Math.max(0, 1 - ex.t / ex.max);
    if (ex.t >= ex.max) {
      scene.remove(ex.light);
      ex.light.dispose();
      cityExplosions.splice(i, 1);
    }
  }

  for (let i = cityEffectBursts.length - 1; i >= 0; i--) {
    const b = cityEffectBursts[i];
    b.t += dtSim;
    const k = Math.min(1, b.t / b.max);
    const fade = Math.max(0, 1 - k * 1.05);

    if (b.kind === 'nuclear') {
      const ease = 1 - (1 - k) * (1 - k);
      const ch = b.group.children;
      if (ch[0]?.isMesh && ch[0].material) {
        ch[0].scale.setScalar(0.32 + ease * 19);
        ch[0].material.opacity = fade * 0.92;
      }
      if (ch[1]?.isMesh && ch[1].material) {
        ch[1].scale.setScalar(0.42 + ease * 15);
        ch[1].material.opacity = fade * 0.76;
      }
      if (ch[2]?.isMesh && ch[2].material) {
        ch[2].scale.setScalar(0.52 + ease * 12);
        ch[2].material.opacity = fade * 0.68;
      }
      if (ch[3]?.isMesh && ch[3].material) {
        const rs = 0.22 + ease * 46;
        ch[3].scale.set(rs, rs, rs);
        ch[3].material.opacity = fade * 0.58;
      }
      if (ch[4]?.isMesh && ch[4].material) {
        ch[4].scale.set(1, 0.35 + ease * 14, 1);
        ch[4].position.y = 0.32 + ease * 4.2;
        ch[4].material.opacity = fade * 0.52;
      }
      const mainRingScale = 0.22 + ease * 46;
      if (ch[5]?.isMesh && ch[5].material) {
        const t0 = 0.66;
        const t1 = 0.9;
        const tw = THREE.MathUtils.clamp((k - t0) / (t1 - t0), 0, 1);
        const rush = tw * tw * (3 - 2 * tw);
        const finScale = mainRingScale * (1 + rush * 2.4) + rush * rush * 34;
        ch[5].scale.set(finScale, finScale, finScale);
        const crest = Math.sin(Math.PI * THREE.MathUtils.clamp((k - t0) / 0.2, 0, 1));
        const pop = k > 0.82 && k < 0.94 ? Math.sin(((k - 0.82) / 0.12) * Math.PI) * 0.95 : 0;
        ch[5].material.opacity = fade * Math.min(1, crest * 0.75 + pop + rush * 0.35);
        if (!b.shockDetonated && k >= 0.805) {
          b.shockDetonated = true;
          const lp = new THREE.PointLight(0xfff8f0, 62, 58, 0.42);
          lp.position.copy(b.group.position);
          scene.add(lp);
          cityExplosions.push({ light: lp, t: 0, max: 0.32, baseIntensity: 62 });
        }
      }
      for (let j = 6; j < ch.length; j++) {
        const c = ch[j];
        if (!c.isMesh || !c.material) continue;
        if (c.userData.spread) {
          c.position.addScaledVector(c.userData.spread, dtSim * 0.4);
          c.material.opacity = fade * 0.4;
          c.rotation.x += dtSim * 7.2;
          c.rotation.y += dtSim * 5;
        }
      }
    } else {
      const sc = 0.28 + k * 4.5;
      const ch0 = b.group.children[0];
      const ch1 = b.group.children[1];
      if (ch0?.isMesh && ch0.material) {
        ch0.scale.setScalar(sc);
        ch0.material.opacity = fade * 0.82;
      }
      if (ch1?.isMesh && ch1.material) {
        ch1.scale.setScalar(sc * 0.88);
        ch1.material.opacity = fade * 0.65;
      }
      for (let j = 2; j < b.group.children.length; j++) {
        const ch = b.group.children[j];
        if (!ch.isMesh || !ch.material) continue;
        if (ch.userData.spread) {
          ch.position.addScaledVector(ch.userData.spread, dtSim * 0.48);
          ch.material.opacity = fade * 0.48;
          ch.rotation.x += dtSim * 6.5;
          ch.rotation.y += dtSim * 4.5;
        }
      }
    }

    if (b.t >= b.max) {
      scene.remove(b.group);
      deepDisposeCityProp(b.group);
      cityEffectBursts.splice(i, 1);
    }
  }

  const grav = CONFIG.city.tracerGravity * (0.9 + ballSpeedMul * 0.1);
  const pad = CONFIG.city.rocketRadius;
  const maxStep = CONFIG.city.tracerMaxStep;

  for (let ri = cityRockets.length - 1; ri >= 0; ri--) {
    const r = cityRockets[ri];
    r.age += dtSim;
    if (r.age > CONFIG.city.rocketLife) {
      disposeRocket(r);
      cityRockets.splice(ri, 1);
      continue;
    }
    r.vel.y -= grav * dtSim;
    const vl = r.vel.length();
    if (vl < 0.35) {
      disposeRocket(r);
      cityRockets.splice(ri, 1);
      continue;
    }
    _bombStepDir.copy(r.vel).multiplyScalar(1 / vl);
    const totalDist = vl * dtSim;
    _bombP0.copy(r.group.position);
    let hitThisFrame = false;
    let traveled = 0;
    while (traveled < totalDist - 1e-8) {
      const seg = Math.min(maxStep, totalDist - traveled);
      _bombP1.copy(_bombP0).addScaledVector(_bombStepDir, seg);
      const hitM = findClosestMonsterOnSegment(_bombP0, _bombP1, pad);
      const hitB = findClosestBuildingOnSegment(_bombP0, _bombP1, pad);
      let hitFirst = null;
      if (hitM && hitB) {
        hitFirst = hitM.u <= hitB.u ? { kind: 'm', ...hitM } : { kind: 'b', ...hitB };
      } else if (hitM) hitFirst = { kind: 'm', ...hitM };
      else if (hitB) hitFirst = { kind: 'b', ...hitB };

      if (hitFirst) {
        r.group.position.copy(hitFirst.point);
        r.group.quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), _bombStepDir);
        disposeRocket(r);
        cityRockets.splice(ri, 1);
        if (hitFirst.kind === 'm') {
          damageCityMonsterFromRocket(hitFirst.mi, hitFirst.point);
        } else {
          spawnStrikeImpactAt(hitFirst.point);
          removeCityBuildingAtIndex(hitFirst.bi, 'full');
          updateCityHud();
        }
        hitThisFrame = true;
        break;
      }
      _bombP0.copy(_bombP1);
      traveled += seg;
    }
    if (hitThisFrame) continue;

    r.group.position.copy(_bombP0);
    r.group.quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), _bombStepDir);

    if (r.group.position.y < 0.04) {
      spawnStrikeImpactAt(r.group.position.clone().setY(0.06));
      disposeRocket(r);
      cityRockets.splice(ri, 1);
      continue;
    }
    const rb = Math.max(CONFIG.city.worldBoundX ?? 200, CONFIG.city.worldBoundZ ?? 200) + 120;
    if (Math.abs(r.group.position.x) > rb || Math.abs(r.group.position.z) > rb) {
      disposeRocket(r);
      cityRockets.splice(ri, 1);
    }
  }

  if (
    !cityFrozen &&
    CONFIG.city.monsterCount > 0 &&
    cityMonsters.length === 0
  ) {
    showCityBanner('¡Monstruos eliminados! Siguiente oleada…', 2600);
    cityFrozen = true;
    cityMonsterWavePendingRespawn = true;
    return;
  }

  if (!CONFIG.city.infiniteAmmo && cityAmmo <= 0 && cityRockets.length === 0 && cityBuildingCount > 0) {
    showCityBanner('Sin munición. Menú para reintentar.', 3400);
    cityFrozen = true;
    cityLose = true;
    return;
  }

  if (cityBuildingCount === 0) {
    showCityBanner('¡Ciudad limpia! Infinito: nueva manzana…', 2400);
    cityFrozen = true;
  }
}

let lastFrameMs = performance.now();
renderer.setAnimationLoop(() => {
  const nowMs = performance.now();
  const dt = Math.max(1e-6, Math.min((nowMs - lastFrameMs) / 1000, 0.08));
  lastFrameMs = nowMs;
  if (gameMode === 'pong') updatePong(dt);
  else if (gameMode === 'city') updateCity(dt);
  renderer.render(scene, camera);
});

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
});

disposeCityWeapon();

/* === Pong: control táctil/drag de paletas (raycast a plano del suelo del campo) === */
const _padPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const _padRay = new THREE.Raycaster();
const _padNdc = new THREE.Vector2();
const _padHit = new THREE.Vector3();

function pointerToCourtZ(clientX, clientY) {
  const rect = renderer.domElement.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;
  _padNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
  _padNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  _padRay.setFromCamera(_padNdc, camera);
  return _padRay.ray.intersectPlane(_padPlane, _padHit) ? _padHit.z : null;
}

function pongTouchPaddleSide(clientX) {
  if (vsRobot) return 'player';
  return clientX < window.innerWidth / 2 ? 'player' : 'ai';
}

renderer.domElement.addEventListener('pointerdown', (e) => {
  if (gameMode !== 'pong') return;
  if (e.pointerType === 'mouse' && e.button !== 0) return;
  const z = pointerToCourtZ(e.clientX, e.clientY);
  if (z == null) return;
  const side = pongTouchPaddleSide(e.clientX);
  if (side === 'player') {
    if (touchPlayerPointerId !== null && touchPlayerPointerId !== e.pointerId) return;
    touchPlayerPointerId = e.pointerId;
    touchPlayerTargetZ = z;
  } else {
    if (touchAiPointerId !== null && touchAiPointerId !== e.pointerId) return;
    touchAiPointerId = e.pointerId;
    touchAiTargetZ = z;
  }
  resumeAudio();
});

renderer.domElement.addEventListener('pointermove', (e) => {
  if (gameMode !== 'pong') return;
  let side = null;
  if (e.pointerId === touchPlayerPointerId) side = 'player';
  else if (e.pointerId === touchAiPointerId) side = 'ai';
  if (side === null) return;
  const z = pointerToCourtZ(e.clientX, e.clientY);
  if (z == null) return;
  if (side === 'player') touchPlayerTargetZ = z;
  else touchAiTargetZ = z;
});

function endPongPointer(e) {
  if (e.pointerId === touchPlayerPointerId) {
    touchPlayerPointerId = null;
    touchPlayerTargetZ = null;
  } else if (e.pointerId === touchAiPointerId) {
    touchAiPointerId = null;
    touchAiTargetZ = null;
  }
}
renderer.domElement.addEventListener('pointerup', endPongPointer);
renderer.domElement.addEventListener('pointercancel', endPongPointer);
renderer.domElement.addEventListener('pointerleave', endPongPointer);

/* Botones +/- de velocidad: el handler vive en _btnDelegateActions arriba. */

renderer.domElement.addEventListener('mousedown', (e) => {
  if (gameMode !== 'city' || cityFrozen || e.button !== 0) return;
  if (document.pointerLockElement !== renderer.domElement) {
    renderer.domElement.requestPointerLock?.();
    return;
  }
  if (CONFIG.city.nuclearRapidFire) {
    cityFireHeld = true;
  } else {
    fireRocket();
  }
});

window.addEventListener('mouseup', (e) => {
  if (e.button !== 0) return;
  cityFireHeld = false;
});

document.addEventListener('pointerlockchange', () => {
  if (document.pointerLockElement !== renderer.domElement) cityFireHeld = false;
});

document.addEventListener('mousemove', (e) => {
  if (gameMode !== 'city' || cityFrozen) return;
  if (document.pointerLockElement !== renderer.domElement) return;
  cityYaw -= e.movementX * 0.0022;
  cityPitch -= e.movementY * 0.0022;
  cityPitch = THREE.MathUtils.clamp(cityPitch, -0.72, 0.75);
});

openMenu();
